import { useState, useEffect, useRef } from "react";
import { supabase } from "./pickfox-app/src/lib/supabase.js";

// ============ DATA ============

// Fallback used during the brief moment before live data arrives from Supabase.
// "ALL" is a UI-only filter (not a real league row in the DB) so it stays here.
const FALLBACK_LEAGUES = [
  { code: "ALL", name: "All", flag: "🌍" },
  { code: "PL",  name: "Premier League", flag: "🏴󠁧󠁢󠁥󠁮󠁧󠁿" },
  { code: "LL",  name: "La Liga", flag: "🇪🇸" },
  { code: "SA",  name: "Serie A", flag: "🇮🇹" },
  { code: "BL",  name: "Bundesliga", flag: "🇩🇪" },
  { code: "SL",  name: "Superliga", flag: "🇩🇰" },
];

// Fallback round metadata used during loading or when no round is open.
// Live values come from the rounds table (see Pickfox component).
const FALLBACK_ROUND = {
  number: 24,
  endsInMs: 1000 * 60 * 60 * 38 + 1000 * 60 * 23, // ~38h 23m
  totalPlayers: 8472,
  myPosition: 247,
  myPoints: 3820,
  pointsToBronze: 360,
  maxPicks: 5,
};

const makeLayers = (matchup) => matchup.layers;

// ============ MATCH STATS ============
// Placeholder data — erstattes med football-data.org responses i prod.
// Format: form = sidste 5 ("V"=vundet, "U"=uafgjort, "T"=tabt), table = { pos, of, points },
// h2h = sidste 3 indbyrdes ("V"=hjemmebanehold vandt, "U"=uafgjort, "T"=hjemmebanehold tabte)
const MATCH_STATS = {
  1: {
    homeForm: ["V","V","U","V","T"], awayForm: ["V","V","V","U","V"],
    homeTable: { pos: 2, of: 20, points: 47 }, awayTable: { pos: 1, of: 20, points: 53 },
    h2h: ["V","T","U"], h2hLabel: "Last 3",
  },
  2: {
    homeForm: ["V","T","V","V","U"], awayForm: ["V","V","T","V","V"],
    homeTable: { pos: 1, of: 20, points: 58 }, awayTable: { pos: 2, of: 20, points: 56 },
    h2h: ["U","V","T"], h2hLabel: "Last 3",
  },
  3: {
    homeForm: ["V","V","V","U","V"], awayForm: ["U","V","T","V","U"],
    homeTable: { pos: 1, of: 20, points: 62 }, awayTable: { pos: 4, of: 20, points: 48 },
    h2h: ["V","V","U"], h2hLabel: "Last 3",
  },
  4: {
    homeForm: ["V","V","V","V","V"], awayForm: ["T","V","V","U","T"],
    homeTable: { pos: 1, of: 18, points: 64 }, awayTable: { pos: 3, of: 18, points: 49 },
    h2h: ["V","V","T"], h2hLabel: "Last 3",
  },
  5: {
    homeForm: ["V","V","U","V","T"], awayForm: ["U","T","V","U","T"],
    homeTable: { pos: 2, of: 12, points: 47 }, awayTable: { pos: 5, of: 12, points: 38 },
    h2h: ["V","U","V"], h2hLabel: "Last 3",
  },
  6: {
    homeForm: ["V","T","V","U","V"], awayForm: ["V","V","U","V","T"],
    homeTable: { pos: 4, of: 20, points: 51 }, awayTable: { pos: 2, of: 20, points: 56 },
    h2h: ["T","V","U"], h2hLabel: "Last 3",
  },
  7: {
    homeForm: ["V","V","T","V","V"], awayForm: ["V","U","V","T","V"],
    homeTable: { pos: 3, of: 20, points: 52 }, awayTable: { pos: 1, of: 20, points: 60 },
    h2h: ["U","T","V"], h2hLabel: "Last 3",
  },
};

// Fallback match list used during loading. Live values come from the
// matches table (see Pickfox component). Once the live round loads,
// these are replaced; if no round is open, the fallback stays.
const FALLBACK_MATCHES = [
  {
    id: 1,
    league: "PL",
    home: "Liverpool", away: "Man City",
    homeShort: "LIV", awayShort: "MCI",
    time: "17:30", date: "Sunday",
    isBonus: true,
    layers: [
      { label: "Match result", options: [
        { label: "Liverpool wins", odds: 2.3, icon: "🔴" },
        { label: "Draw", odds: 3.6, icon: "⚖️" },
        { label: "Man City wins", odds: 2.6, icon: "🩵" },
      ]},
      { label: "Goals in match", options: [
        { label: "Over 2.5 goals", odds: 1.65, icon: "🔥" },
        { label: "Under 2.5 goals", odds: 2.2, icon: "🧊" },
        { label: "Both teams score", odds: 1.8, icon: "⚡" },
      ]},
      { label: "Risky prop", options: [
        { label: "Salah 2+ goals", odds: 4.5, icon: "⚽" },
        { label: "Player sent off", odds: 5.0, icon: "🟥" },
        { label: "Goal after 85'", odds: 3.5, icon: "⏱️" },
      ]},
      // Alternative risky reroll options
      { rerollOptions: [
        { label: "Salah hat-trick", odds: 8.5, icon: "🎩" },
        { label: "Both Salah & Haaland score", odds: 3.8, icon: "🎯" },
        { label: "Penalty awarded", odds: 3.2, icon: "⚪" },
      ]},
    ],
  },
  {
    id: 2,
    league: "LL",
    home: "FC Barcelona", away: "Real Madrid",
    homeShort: "FCB", awayShort: "RMA",
    time: "21:00", date: "Saturday",
    layers: [
      { label: "Match result", options: [
        { label: "FCB wins", odds: 2.1, icon: "🔵🔴" },
        { label: "Draw", odds: 3.4, icon: "⚖️" },
        { label: "Real wins", odds: 3.2, icon: "⚪" },
      ]},
      { label: "Goals in match", options: [
        { label: "Over 2.5 goals", odds: 1.75, icon: "🔥" },
        { label: "Under 2.5 goals", odds: 2.1, icon: "🧊" },
        { label: "Both teams score", odds: 1.9, icon: "⚡" },
      ]},
      { label: "Risky prop", options: [
        { label: "Lewandowski 2+ goals", odds: 5.0, icon: "⚽" },
        { label: "Mbappé MOTM", odds: 4.2, icon: "🌟" },
        { label: "Red card in match", odds: 4.5, icon: "🟥" },
      ]},
      { rerollOptions: [
        { label: "Lewandowski scores first", odds: 3.5, icon: "🥇" },
        { label: "Over 12 yellow cards", odds: 4.5, icon: "🟨" },
        { label: "Bellingham assist + goal", odds: 5.5, icon: "🎯" },
      ]},
    ],
  },
  {
    id: 3,
    league: "SA",
    home: "Inter", away: "Juventus",
    homeShort: "INT", awayShort: "JUV",
    time: "20:45", date: "Sunday",
    layers: [
      { label: "Match result", options: [
        { label: "Inter wins", odds: 1.9, icon: "⚫🔵" },
        { label: "Draw", odds: 3.3, icon: "⚖️" },
        { label: "Juventus wins", odds: 3.8, icon: "⚪⚫" },
      ]},
      { label: "Goals in match", options: [
        { label: "Over 2.5 goals", odds: 1.9, icon: "🔥" },
        { label: "Under 2.5 goals", odds: 1.95, icon: "🧊" },
        { label: "Clean sheet", odds: 2.5, icon: "🛡️" },
      ]},
      { label: "Risky prop", options: [
        { label: "Lautaro 2+ goals", odds: 5.5, icon: "⚽" },
        { label: "Vlahović scores first", odds: 3.2, icon: "🥇" },
        { label: "Barella assist", odds: 3.8, icon: "🎯" },
      ]},
      { rerollOptions: [
        { label: "Both keepers save penalty", odds: 12.0, icon: "🧤" },
        { label: "Goal after 90'", odds: 4.5, icon: "⏱️" },
        { label: "Draw at HT, Inter wins", odds: 4.2, icon: "🔄" },
      ]},
    ],
  },
  {
    id: 4,
    league: "BL",
    home: "Bayern", away: "Dortmund",
    homeShort: "BAY", awayShort: "BVB",
    time: "18:30", date: "Saturday",
    layers: [
      { label: "Match result", options: [
        { label: "Bayern wins", odds: 1.6, icon: "🔴" },
        { label: "Draw", odds: 4.2, icon: "⚖️" },
        { label: "Dortmund wins", odds: 4.5, icon: "🟡" },
      ]},
      { label: "Goals in match", options: [
        { label: "Over 3.5 goals", odds: 2.0, icon: "🔥" },
        { label: "Under 3.5 goals", odds: 1.8, icon: "🧊" },
        { label: "Both teams score", odds: 1.5, icon: "⚡" },
      ]},
      { label: "Risky prop", options: [
        { label: "Kane 2+ goals", odds: 3.5, icon: "⚽" },
        { label: "Dortmund player sent off", odds: 5.5, icon: "🟥" },
        { label: "Goal in stoppage time", odds: 3.8, icon: "⏱️" },
      ]},
      { rerollOptions: [
        { label: "Kane hat-trick", odds: 9.0, icon: "🎩" },
        { label: "Musiala 2+ assists", odds: 5.0, icon: "🎯" },
        { label: "Over 12 yellow cards", odds: 4.2, icon: "🟨" },
      ]},
    ],
  },
  {
    id: 6,
    league: "SL",
    home: "FC København", away: "Brøndby",
    homeShort: "FCK", awayShort: "BIF",
    time: "14:00", date: "Sunday",
    layers: [
      { label: "Match result", options: [
        { label: "FCK wins", odds: 1.8, icon: "⚪🔵" },
        { label: "Draw", odds: 3.5, icon: "⚖️" },
        { label: "Brøndby wins", odds: 4.0, icon: "🟡🔵" },
      ]},
      { label: "Goals in match", options: [
        { label: "Over 2.5 goals", odds: 2.0, icon: "🔥" },
        { label: "Under 2.5 goals", odds: 1.85, icon: "🧊" },
        { label: "Both teams score", odds: 1.95, icon: "⚡" },
      ]},
      { label: "Risky prop", options: [
        { label: "Red card in derby", odds: 3.5, icon: "🟥" },
        { label: "Late goal after 85'", odds: 2.8, icon: "⏱️" },
        { label: "FCK 3+ goals", odds: 5.0, icon: "🚀" },
      ]},
      { rerollOptions: [
        { label: "FCK wins 3-1 exactly", odds: 12.0, icon: "🎯" },
        { label: "Penalty awarded", odds: 3.8, icon: "⚪" },
        { label: "Brøndby leads at HT, FCK wins", odds: 6.0, icon: "🔄" },
      ]},
    ],
  },
  {
    id: 7,
    league: "LL",
    home: "Atlético", away: "Sevilla",
    homeShort: "ATM", awayShort: "SEV",
    time: "16:15", date: "Saturday",
    layers: [
      { label: "Match result", options: [
        { label: "Atlético wins", odds: 1.7, icon: "🔴⚪" },
        { label: "Draw", odds: 3.6, icon: "⚖️" },
        { label: "Sevilla wins", odds: 5.0, icon: "⚪🔴" },
      ]},
      { label: "Goals in match", options: [
        { label: "Under 2.5 goals", odds: 1.7, icon: "🧊" },
        { label: "Over 2.5 goals", odds: 2.1, icon: "🔥" },
        { label: "Clean sheet", odds: 2.3, icon: "🛡️" },
      ]},
      { label: "Risky prop", options: [
        { label: "Griezmann 2+ goals", odds: 5.0, icon: "⚽" },
        { label: "Red card in match", odds: 4.0, icon: "🟥" },
        { label: "Only 1 goal in match", odds: 4.2, icon: "1️⃣" },
      ]},
      { rerollOptions: [
        { label: "Simeone red card", odds: 12.0, icon: "🟥" },
        { label: "Griezmann hat-trick", odds: 10.0, icon: "🎩" },
        { label: "Double clean sheet (0-0)", odds: 6.5, icon: "🛡️" },
      ]},
    ],
  },
  {
    id: 8,
    league: "SA",
    home: "Napoli", away: "Milan",
    homeShort: "NAP", awayShort: "MIL",
    time: "18:00", date: "Sunday",
    layers: [
      { label: "Match result", options: [
        { label: "Napoli wins", odds: 2.1, icon: "🔵" },
        { label: "Draw", odds: 3.4, icon: "⚖️" },
        { label: "Milan wins", odds: 3.2, icon: "🔴⚫" },
      ]},
      { label: "Goals in match", options: [
        { label: "Over 2.5 goals", odds: 1.8, icon: "🔥" },
        { label: "Under 2.5 goals", odds: 2.0, icon: "🧊" },
        { label: "Both teams score", odds: 1.7, icon: "⚡" },
      ]},
      { label: "Risky prop", options: [
        { label: "Leão 2+ goals", odds: 5.5, icon: "⚽" },
        { label: "Kvaratskhelia MOTM + goal", odds: 4.5, icon: "🌟" },
        { label: "Goal in first 10 min.", odds: 3.5, icon: "⏱️" },
      ]},
      { rerollOptions: [
        { label: "Both Leão & Kvaratskhelia score", odds: 4.2, icon: "🎯" },
        { label: "Red card in 1st half", odds: 6.0, icon: "🟥" },
        { label: "Over 5 corners per team", odds: 3.8, icon: "🚩" },
      ]},
    ],
  },
];

// ============ SPONSORS ============
// Hver sponsor har en `logo` funktion der returnerer JSX. Når vi går live
// erstatter vi disse med <img src={sponsor.logoUrl} /> fra Supabase storage.

const SPONSORS = {
  nordplay: {
    name: "NordPlay",
    brand: "#1e3a5f",
    logo: ({ inverted = false, scale = 1 } = {}) => (
      <svg width={92 * scale} height={22 * scale} viewBox="0 0 110 26" xmlns="http://www.w3.org/2000/svg">
        <circle cx="13" cy="13" r="10" fill={inverted ? "#fafaf9" : "#1e3a5f"} />
        <polygon points="10,8 10,18 18,13" fill={inverted ? "#1c1917" : "#fafaf9"} />
        <text x="29" y="18" fontFamily="system-ui, sans-serif" fontSize="14" fontWeight="600" fill={inverted ? "#fafaf9" : "#1e3a5f"}>NordPlay</text>
      </svg>
    ),
  },
  atletik: {
    name: "Atletik",
    brand: "#dc2626",
    logo: ({ scale = 1 } = {}) => (
      <svg width={84 * scale} height={24 * scale} viewBox="0 0 100 28" xmlns="http://www.w3.org/2000/svg">
        <polygon points="3,22 9,6 13,6 7,22" fill="#dc2626" />
        <polygon points="12,22 18,6 22,6 16,22" fill="#dc2626" />
        <polygon points="21,22 27,6 31,6 25,22" fill="#dc2626" />
        <text x="36" y="20" fontFamily="system-ui, sans-serif" fontSize="13" fontWeight="700" fill="#1c1917">ATLETIK</text>
      </svg>
    ),
  },
  bryghus: {
    name: "Bryghus K",
    brand: "#7c2d12",
    logo: ({ scale = 1 } = {}) => (
      <svg width={80 * scale} height={24 * scale} viewBox="0 0 92 28" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="2" width="22" height="22" rx="3" fill="#7c2d12" />
        <text x="11" y="20" fontFamily="serif" fontSize="17" fontWeight="700" fill="#fde68a" textAnchor="middle">K</text>
        <text x="28" y="13" fontFamily="system-ui, sans-serif" fontSize="9" fontWeight="500" fill="#7c2d12" letterSpacing="0.6">BRYGHUS</text>
        <text x="28" y="22" fontFamily="system-ui, sans-serif" fontSize="7" fill="#a16207" letterSpacing="0.4">est. 1923</text>
      </svg>
    ),
  },
};

const ROUND_SPONSOR_IDS = ["nordplay", "atletik", "bryghus"];

// ============ PRIZE TIERS ============

const PRIZE_TIERS = [
  { id: "gold",   name: "Gold",    range: "#1",      letter: "G", bg: "#d97706", fg: "#fff",     labelColor: "#b45309",
    prize: "2 × FCK tickets", prizeDetail: "Tribune A · next home game at Parken", sponsorId: "nordplay" },
  { id: "silver", name: "Silver",  range: "#2-3",    letter: "S", bg: "#d6d3d1", fg: "#44403c",  labelColor: "#57534e",
    prize: "DKK 500 gift card", prizeDetail: "Sports store · your choice", sponsorId: "atletik" },
  { id: "bronze", name: "Bronze",  range: "#4-10",   letter: "B", bg: "#fcd34d", fg: "#78350f",  labelColor: "#92400e",
    prize: "2 cinema tickets", prizeDetail: "All Nordisk Film cinemas", sponsorId: "bryghus" },
  { id: "topten", name: "Top 10 %", range: "~#847+", letter: "★", bg: "#faf7ef", fg: "#78716c",  labelColor: "#78716c",
    prize: "Sponsor discount codes", prizeDetail: "10–20 % at all 3 sponsors", sponsorId: null },
];

const tierById = (id) => PRIZE_TIERS.find((t) => t.id === id);
const headlineTier = PRIZE_TIERS[0];

// Fallback leaderboard — vises kun briefly før live-data loader, eller hvis ingen runde er aktiv.
const FALLBACK_LEADERBOARD = [];

// ============ PLAYER DATA ============
// Profil + runde-historik for hver spiller på leaderboardet.
// I prod: hentes fra Supabase pr. tap. Round 24 = aktuelt locked.
// Pick-history viser hvad de pickede + V (vundet) / T (tabt) / null (skipped lag 3).

const PLAYER_DATA = {
  "Magnus K.": {
    avatar: { initials: "MK", color: "#dc2626", bg: "#fee2e2" },
    joined: "Sep 2025",
    roundsPlayed: 22,
    bestFinish: { round: 19, position: 1, tier: "gold" },
    avgPosition: 142,
    streak: 23,
    activeRound: { picksMade: 5, of: 5, layersFilled: 15, of_layers: 15 },
    history: [
      { round: 23, position: 1, points: 4650, tier: "gold", picks: [
        { match: "FCK vs BIF", isBonus: true, layers: [
          { label: "FCK wins", odds: 1.9, result: "V" },
          { label: "Both teams score", odds: 1.85, result: "V" },
          { label: "Goal after 85'", odds: 3.5, result: "T" },
        ], roundPoints: 555 },
        { match: "LIV vs ARS", isBonus: false, layers: [
          { label: "Both teams score", odds: 1.7, result: "V" },
          { label: "Over 2.5 goals", odds: 1.6, result: "V" },
          { label: "Salah scores first", odds: 3.8, result: "V" },
        ], roundPoints: 893 },
        { match: "BAY vs BVB", isBonus: false, layers: [
          { label: "Bayern wins", odds: 1.5, result: "V" },
          { label: "Over 3.5 goals", odds: 2.1, result: "V" },
          { label: "Skipped & locked", odds: null, result: null, skipped: true },
        ], roundPoints: 360 },
        { match: "INT vs JUV", isBonus: false, layers: [
          { label: "Inter wins", odds: 1.9, result: "V" },
          { label: "Under 2.5 goals", odds: 2.0, result: "V" },
          { label: "Lautaro 2+ goals", odds: 5.5, result: "T" },
        ], roundPoints: 379 },
        { match: "ATM vs SEV", isBonus: false, layers: [
          { label: "Atletico wins", odds: 1.65, result: "V" },
          { label: "Under 2.5 goals", odds: 1.85, result: "V" },
          { label: "Griezmann scores", odds: 2.4, result: "V" },
        ], roundPoints: 432 },
      ]},
      { round: 22, position: 8, points: 3120, tier: "bronze" },
      { round: 21, position: 184, points: 2410, tier: null },
    ],
  },
  "Emma L.": {
    avatar: { initials: "EL", color: "#7c3aed", bg: "#ede9fe" },
    joined: "Aug 2025",
    roundsPlayed: 31,
    bestFinish: { round: 14, position: 2, tier: "silver" },
    avgPosition: 287,
    streak: 12,
    activeRound: { picksMade: 5, of: 5, layersFilled: 14, of_layers: 15 },
    history: [
      { round: 23, position: 2, points: 4480, tier: "silver", picks: [
        { match: "FCK vs BIF", isBonus: true, layers: [
          { label: "Draw", odds: 3.4, result: "T" },
          { label: "Under 2.5 goals", odds: 2.2, result: "V" },
          { label: "Skipped & locked", odds: null, result: null, skipped: true },
        ], roundPoints: 495 },
        { match: "LIV vs ARS", isBonus: false, layers: [
          { label: "Liverpool wins", odds: 1.95, result: "V" },
          { label: "Both teams score", odds: 1.7, result: "V" },
          { label: "Salah 2+ goals", odds: 4.5, result: "T" },
        ], roundPoints: 364 },
        { match: "BAY vs BVB", isBonus: false, layers: [
          { label: "Bayern wins", odds: 1.5, result: "V" },
          { label: "Over 2.5 goals", odds: 1.45, result: "V" },
          { label: "Kane scores", odds: 1.9, result: "V" },
        ], roundPoints: 484 },
        { match: "FCB vs RMA", isBonus: false, layers: [
          { label: "Real wins", odds: 3.2, result: "V" },
          { label: "Over 2.5 goals", odds: 1.75, result: "V" },
          { label: "Bellingham scores", odds: 2.8, result: "V" },
        ], roundPoints: 775 },
        { match: "NAP vs MIL", isBonus: false, layers: [
          { label: "Napoli wins", odds: 1.85, result: "V" },
          { label: "Under 2.5 goals", odds: 1.95, result: "V" },
          { label: "Leão MOTM + goal", odds: 4.5, result: "T" },
        ], roundPoints: 380 },
      ]},
      { round: 22, position: 24, points: 3340, tier: null },
      { round: 21, position: 5, points: 4180, tier: "bronze" },
    ],
  },
  "Jonas B.": {
    avatar: { initials: "JB", color: "#0891b2", bg: "#cffafe" },
    joined: "Oct 2025",
    roundsPlayed: 18,
    bestFinish: { round: 23, position: 3, tier: "silver" },
    avgPosition: 412,
    streak: 6,
    activeRound: { picksMade: 4, of: 5, layersFilled: 11, of_layers: 12 },
    history: [
      { round: 23, position: 3, points: 4310, tier: "silver", picks: [
        { match: "FCK vs BIF", isBonus: true, layers: [
          { label: "FCK wins", odds: 1.9, result: "V" },
          { label: "Over 2.5 goals", odds: 1.8, result: "T" },
          { label: "Skipped & locked", odds: null, result: null, skipped: true },
        ], roundPoints: 285 },
        { match: "INT vs JUV", isBonus: false, layers: [
          { label: "Inter wins", odds: 1.9, result: "V" },
          { label: "Under 2.5 goals", odds: 2.0, result: "V" },
          { label: "Vlahović scores first", odds: 3.2, result: "V" },
        ], roundPoints: 696 },
        { match: "BAY vs BVB", isBonus: false, layers: [
          { label: "Bayern wins", odds: 1.5, result: "V" },
          { label: "Both teams score", odds: 2.0, result: "V" },
          { label: "Goal in first 10 min", odds: 4.0, result: "T" },
        ], roundPoints: 300 },
        { match: "ATM vs SEV", isBonus: false, layers: [
          { label: "Atletico wins", odds: 1.65, result: "V" },
          { label: "Both teams score", odds: 1.95, result: "V" },
          { label: "Griezmann scores", odds: 2.4, result: "V" },
        ], roundPoints: 562 },
      ]},
      { round: 22, position: 47, points: 3010, tier: null },
    ],
  },
  "Sofia H.": {
    avatar: { initials: "SH", color: "#16a34a", bg: "#dcfce7" },
    joined: "Nov 2025",
    roundsPlayed: 14,
    bestFinish: { round: 23, position: 4, tier: "bronze" },
    avgPosition: 521,
    streak: 4,
    activeRound: { picksMade: 5, of: 5, layersFilled: 13, of_layers: 15 },
    history: [
      { round: 23, position: 4, points: 4250, tier: "bronze", picks: [
        { match: "LIV vs ARS", isBonus: false, layers: [
          { label: "Liverpool wins", odds: 1.95, result: "V" },
          { label: "Over 2.5 goals", odds: 1.6, result: "V" },
          { label: "Skipped & locked", odds: null, result: null, skipped: true },
        ], roundPoints: 312 },
        { match: "FCK vs BIF", isBonus: true, layers: [
          { label: "FCK wins", odds: 1.9, result: "V" },
          { label: "Both teams score", odds: 1.85, result: "V" },
          { label: "Skipped & locked", odds: null, result: null, skipped: true },
        ], roundPoints: 562 },
        { match: "FCB vs RMA", isBonus: false, layers: [
          { label: "FCB wins", odds: 2.1, result: "T" },
          { label: "Over 2.5 goals", odds: 1.75, result: "V" },
          { label: "Lewandowski 2+ goals", odds: 5.0, result: "T" },
        ], roundPoints: 175 },
        { match: "BAY vs BVB", isBonus: false, layers: [
          { label: "Bayern wins", odds: 1.5, result: "V" },
          { label: "Over 2.5 goals", odds: 1.45, result: "V" },
          { label: "Kane 2+ goals", odds: 3.5, result: "V" },
        ], roundPoints: 920 },
        { match: "NAP vs MIL", isBonus: false, layers: [
          { label: "Napoli wins", odds: 1.85, result: "V" },
          { label: "Under 2.5 goals", odds: 1.95, result: "V" },
          { label: "Skipped & locked", odds: null, result: null, skipped: true },
        ], roundPoints: 360 },
      ]},
    ],
  },
  "Lukas P.": {
    avatar: { initials: "LP", color: "#ea580c", bg: "#ffedd5" },
    joined: "Sep 2025",
    roundsPlayed: 19,
    bestFinish: { round: 17, position: 2, tier: "silver" },
    avgPosition: 298,
    streak: 9,
    activeRound: { picksMade: 5, of: 5, layersFilled: 15, of_layers: 15 },
    history: [
      { round: 23, position: 5, points: 4180, tier: "bronze", picks: [
        { match: "FCK vs BIF", isBonus: true, layers: [
          { label: "BIF wins", odds: 4.2, result: "T" },
          { label: "Over 2.5 goals", odds: 1.8, result: "V" },
          { label: "Skipped & locked", odds: null, result: null, skipped: true },
        ], roundPoints: 270 },
        { match: "LIV vs ARS", isBonus: false, layers: [
          { label: "Liverpool wins", odds: 1.95, result: "V" },
          { label: "Both teams score", odds: 1.7, result: "V" },
          { label: "Salah scores first", odds: 3.8, result: "V" },
        ], roundPoints: 1259 },
        { match: "BAY vs BVB", isBonus: false, layers: [
          { label: "Bayern wins", odds: 1.5, result: "V" },
          { label: "Both teams score", odds: 2.0, result: "V" },
          { label: "Skipped & locked", odds: null, result: null, skipped: true },
        ], roundPoints: 350 },
        { match: "INT vs JUV", isBonus: false, layers: [
          { label: "Inter wins", odds: 1.9, result: "V" },
          { label: "Under 2.5 goals", odds: 2.0, result: "V" },
          { label: "Skipped & locked", odds: null, result: null, skipped: true },
        ], roundPoints: 390 },
        { match: "ATM vs SEV", isBonus: false, layers: [
          { label: "Atletico wins", odds: 1.65, result: "V" },
          { label: "Both teams score", odds: 1.95, result: "V" },
          { label: "Griezmann scores", odds: 2.4, result: "V" },
        ], roundPoints: 562 },
      ]},
    ],
  },
  // Ranks 6-10 — minimal data, ingen detaljerede picks
  "Mikkel N.": {
    avatar: { initials: "MN", color: "#0369a1", bg: "#e0f2fe" },
    joined: "Jan 2026", roundsPlayed: 8, bestFinish: { round: 22, position: 6, tier: "bronze" },
    avgPosition: 348, streak: 8,
    activeRound: { picksMade: 5, of: 5, layersFilled: 14, of_layers: 15 },
    history: [{ round: 23, position: 6, points: 4090, tier: "bronze" }],
  },
  "Anna T.": {
    avatar: { initials: "AT", color: "#be185d", bg: "#fce7f3" },
    joined: "Oct 2025", roundsPlayed: 21, bestFinish: { round: 11, position: 4, tier: "bronze" },
    avgPosition: 198, streak: 14,
    activeRound: { picksMade: 5, of: 5, layersFilled: 15, of_layers: 15 },
    history: [{ round: 23, position: 7, points: 4020, tier: "bronze" }],
  },
  "Frederik S.": {
    avatar: { initials: "FS", color: "#65a30d", bg: "#ecfccb" },
    joined: "Aug 2025", roundsPlayed: 27, bestFinish: { round: 8, position: 3, tier: "silver" },
    avgPosition: 156, streak: 3,
    activeRound: { picksMade: 4, of: 5, layersFilled: 12, of_layers: 12 },
    history: [{ round: 23, position: 8, points: 3970, tier: "bronze" }],
  },
  "Mathilde V.": {
    avatar: { initials: "MV", color: "#9333ea", bg: "#f3e8ff" },
    joined: "Nov 2025", roundsPlayed: 16, bestFinish: { round: 18, position: 5, tier: "bronze" },
    avgPosition: 274, streak: 5,
    activeRound: { picksMade: 5, of: 5, layersFilled: 13, of_layers: 15 },
    history: [{ round: 23, position: 9, points: 3920, tier: "bronze" }],
  },
  "Christian Ø.": {
    avatar: { initials: "CØ", color: "#475569", bg: "#f1f5f9" },
    joined: "Sep 2025", roundsPlayed: 24, bestFinish: { round: 16, position: 7, tier: "bronze" },
    avgPosition: 312, streak: 11,
    activeRound: { picksMade: 5, of: 5, layersFilled: 15, of_layers: 15 },
    history: [{ round: 23, position: 10, points: 3880, tier: "bronze" }],
  },
};

// ============ USER (DIG) ============
// Profil + historik for den loggede ind bruger.
// Active round 24 picks kommer fra `picks` state — historikken er mock-data.

const USER_PROFILE = {
  name: "Mikkel P.",
  handle: "@mikkel",
  avatar: { initials: "MP", color: "#b45309", bg: "#fef3c7" },
  joined: "Jan 2026",
  roundsPlayed: 8,
  bestFinish: { round: 22, position: 6, tier: "bronze" },
  avgPosition: 348,
  streak: 8,
  // Lifetime tier wins
  tierWins: { gold: 0, silver: 0, bronze: 1, topten: 4 },
  totalPointsLifetime: 24580,
};

// Vundne præmier — seed-data til prototype. Senere fra Supabase.
// Status: unclaimed (skal indløses) → claimed (formular indsendt, sponsor leverer) → redeemed (modtaget/brugt).
// expiresAt = ISO date string, 30 dage efter rundens slut. Udløber præmien udover dette droppes den.
const USER_WINS_SEED = [
  {
    id: "win-r23-bronze",
    tier: "bronze",
    roundNumber: 23,
    sponsorId: "bryghus",
    prizeLabel: "2 cinema tickets",
    prizeDetail: "All Nordisk Film cinemas",
    status: "unclaimed",
    expiresAt: "2026-05-29",   // 30 days from today's date in prototype
    claimedAt: null,
    code: null,
  },
  {
    id: "win-r22-topten",
    tier: "topten",
    roundNumber: 22,
    sponsorId: null,
    prizeLabel: "Sponsor discount codes",
    prizeDetail: "10–20 % at NordPlay, Atletik, Bryghus K",
    status: "redeemed",
    expiresAt: "2026-04-15",
    claimedAt: "2026-03-30",
    code: "PICKFOX-FOX23",
  },
];

const USER_HISTORY = [
  { round: 23, position: 412, points: 3450, tier: "topten", picks: [
    { match: "FCK vs BIF", isBonus: true, layers: [
      { label: "FCK wins", odds: 1.9, result: "V" },
      { label: "Both teams score", odds: 1.85, result: "V" },
      { label: "Skipped & locked", odds: null, result: null, skipped: true },
    ], roundPoints: 562 },
    { match: "LIV vs ARS", isBonus: false, layers: [
      { label: "Liverpool wins", odds: 1.95, result: "V" },
      { label: "Over 2.5 goals", odds: 1.6, result: "V" },
      { label: "Salah scores first", odds: 3.8, result: "V" },
    ], roundPoints: 1185 },
    { match: "BAY vs BVB", isBonus: false, layers: [
      { label: "Bayern wins", odds: 1.5, result: "V" },
      { label: "Both teams score", odds: 2.0, result: "V" },
      { label: "Kane 2+ goals", odds: 3.5, result: "T" },
    ], roundPoints: 300 },
    { match: "INT vs JUV", isBonus: false, layers: [
      { label: "Inter wins", odds: 1.9, result: "V" },
      { label: "Under 2.5 goals", odds: 2.0, result: "V" },
      { label: "Lautaro 2+ goals", odds: 5.5, result: "T" },
    ], roundPoints: 379 },
    { match: "ATM vs SEV", isBonus: false, layers: [
      { label: "Sevilla wins", odds: 4.0, result: "T" },
      { label: "Over 2.5 goals", odds: 2.1, result: "T" },
      { label: "Skipped & locked", odds: null, result: null, skipped: true },
    ], roundPoints: 0 },
  ]},
  { round: 22, position: 6, points: 4090, tier: "bronze", picks: [
    { match: "FCK vs RFC", isBonus: true, layers: [
      { label: "FCK wins", odds: 1.7, result: "V" },
      { label: "Over 2.5 goals", odds: 1.85, result: "V" },
      { label: "Cornelius scores", odds: 2.6, result: "V" },
    ], roundPoints: 1228 },
    { match: "MCI vs CHE", isBonus: false, layers: [
      { label: "City wins", odds: 1.6, result: "V" },
      { label: "Both teams score", odds: 1.7, result: "V" },
      { label: "Haaland 2+ goals", odds: 2.8, result: "V" },
    ], roundPoints: 761 },
    { match: "JUV vs MIL", isBonus: false, layers: [
      { label: "Draw", odds: 3.2, result: "V" },
      { label: "Under 2.5 goals", odds: 1.95, result: "V" },
      { label: "Skipped & locked", odds: null, result: null, skipped: true },
    ], roundPoints: 624 },
    { match: "BVB vs RBL", isBonus: false, layers: [
      { label: "Dortmund wins", odds: 2.0, result: "T" },
      { label: "Over 2.5 goals", odds: 1.6, result: "V" },
      { label: "Sancho assist", odds: 4.2, result: "T" },
    ], roundPoints: 160 },
    { match: "BAR vs ATM", isBonus: false, layers: [
      { label: "Barca wins", odds: 1.85, result: "V" },
      { label: "Both teams score", odds: 1.9, result: "V" },
      { label: "Lewandowski scores first", odds: 3.5, result: "V" },
    ], roundPoints: 1267 },
  ]},
  { round: 21, position: 1834, points: 2210, tier: null },
  { round: 20, position: 891, points: 2890, tier: null },
  { round: 19, position: 67, points: 4180, tier: "topten" },
  { round: 18, position: 143, points: 3920, tier: "topten" },
  { round: 17, position: 2104, points: 1870, tier: null },
  { round: 16, position: 412, points: 3380, tier: "topten" },
];

const formatMs = (ms) => {
  const totalMin = Math.floor(ms / 60000);
  const d = Math.floor(totalMin / (60 * 24));
  const h = Math.floor((totalMin % (60 * 24)) / 60);
  const m = totalMin % 60;
  return { d, h, m };
};

// ============ COMPONENT ============

export default function Pickfox({ onOpenOperator } = {}) {
  const [view, setView] = useState("lobby");

  // Live leagues from Supabase. Starts as FALLBACK_LEAGUES so the UI renders
  // immediately, then swaps to live data once the query returns.
  const [LEAGUES, setLEAGUES] = useState(FALLBACK_LEAGUES);
  useEffect(() => {
    supabase
      .from("leagues")
      .select("*")
      .order("sort_order")
      .then(({ data, error }) => {
        if (error) { console.error("Failed to load leagues:", error); return; }
        setLEAGUES([
          FALLBACK_LEAGUES[0],
          ...data.map(l => ({ code: l.id, name: l.name, flag: l.emoji })),
        ]);
      });
  }, []);

  // Live round + matches from Supabase. The active round is the most recent
  // one with status='open'. Falls back to FALLBACK_ROUND/MATCHES while loading
  // or when no round is open. activeRoundId is needed when submitting picks.
  const [ROUND, setROUND] = useState(FALLBACK_ROUND);
  const [MATCHES, setMATCHES] = useState(FALLBACK_MATCHES);
  const [activeRoundId, setActiveRoundId] = useState(null);
  useEffect(() => {
    supabase
      .from("rounds")
      .select("*, matches(*)")
      .eq("status", "open")
      .order("number", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data: r, error }) => {
        if (error) { console.error("Failed to load round:", error); return; }
        if (!r) return;
        setActiveRoundId(r.id);
        setROUND({
          number: r.number,
          endsInMs: Math.max(0, new Date(r.locks_at) - new Date()), // countdown til picks-lock
          totalPlayers: 0,
          myPosition: 0,
          myPoints: 0,
          pointsToBronze: 0,
          maxPicks: r.max_picks,
        });
        const sorted = (r.matches || []).slice().sort((a, b) => a.sort_order - b.sort_order);
        setMATCHES(sorted.map((m) => {
          const dt = new Date(m.kickoff_at);
          return {
            id: m.id,
            league: m.league_id,
            home: m.home_team,
            away: m.away_team,
            homeShort: m.home_short,
            awayShort: m.away_short,
            kickoffAt: m.kickoff_at,
            time: dt.toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" }),
            date: dt.toLocaleDateString("en-US", { weekday: "long" }),
            isBonus: m.is_bonus,
            stats: m.stats,
            layers: [
              { label: "Match result",  options: m.layer1_options },
              { label: "Goals in match", options: m.layer2_options },
              { label: "Risky prop",     options: m.layer3_options },
              ...(m.layer3_reroll ? [{ rerollOptions: m.layer3_reroll }] : []),
            ],
          };
        }));
      });
  }, []); // lobby | round | match | confirm | leaderboard | player | history | profile | claim | submitted

  // Editérbar profil. Navn synkroniseres med profiles-tabellen i Supabase (commitName).
  // Avatar er stadig en lokal data URL — flyttes til Storage-bucket'en senere.
  const [profileName, setProfileName] = useState(USER_PROFILE.name);
  const [profileAvatarUrl, setProfileAvatarUrl] = useState(null); // data URL eller null = brug initialer
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(USER_PROFILE.name);

  // Hent live profile-row for den indloggede bruger og hydrér navne-state'en.
  // Stat-felter (best finish, lifetime points, streak) bliver først levende når
  // runder spilles — for nu læses de stadig fra USER_PROFILE-mocken.
  const [profileRow, setProfileRow] = useState(null);
  const refreshProfileRow = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data, error } = await supabase.from("profiles").select("*").eq("id", user.id).single();
    if (error) { console.error("Failed to refresh profile:", error); return; }
    setProfileRow(data);
  };
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single()
        .then(({ data, error }) => {
          if (cancelled) return;
          if (error) { console.error("Failed to load profile:", error); return; }
          setProfileRow(data);
          if (data.display_name) {
            setProfileName(data.display_name);
            setNameDraft(data.display_name);
          }
        });
    });
    return () => { cancelled = true; };
  }, []);

  // Live profile-fields — falder tilbage til USER_PROFILE-mock indtil profileRow loader.
  const currentHandle = profileRow?.handle ? `@${profileRow.handle}` : USER_PROFILE.handle;
  const currentJoined = profileRow?.joined_at
    ? new Date(profileRow.joined_at).toLocaleDateString("en-US", { month: "short", year: "numeric" })
    : USER_PROFILE.joined;
  const currentTotalPoints = profileRow?.total_points_lifetime ?? USER_PROFILE.totalPointsLifetime;

  // Gem navne-ændring både lokalt og i databasen.
  const commitName = async (newName) => {
    setProfileName(newName);
    if (profileRow?.id) {
      const { error } = await supabase
        .from("profiles")
        .update({ display_name: newName })
        .eq("id", profileRow.id);
      if (error) console.error("Failed to save name:", error);
    }
  };

  // Settings — placeholder state. Will become a row in the 'profiles' table.
  const [settings, setSettings] = useState({
    notifications: true,    // round start, lock in 1h, results
    picksPublic: true,      // others can see your picks after lock
    newsletter: false,
  });

  // Vundne præmier — fetched live fra prize_wins-tabellen. Tom array indtil
  // brugeren faktisk har vundet noget.
  const [wins, setWins] = useState([]);
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user || cancelled) return;
      supabase
        .from("prize_wins")
        .select("*, rounds!inner(number)")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .then(({ data, error }) => {
          if (cancelled) return;
          if (error) { console.error("Failed to load wins:", error); return; }
          setWins((data || []).map(w => ({
            id: w.id,
            tier: w.tier,
            roundNumber: w.rounds?.number,
            sponsorId: w.sponsor_id,
            prizeLabel: w.prize_label,
            prizeDetail: w.prize_detail,
            status: w.status,
            expiresAt: w.expires_at,
            claimedAt: w.claimed_at,
            code: w.redemption_code,
          })));
        });
    });
    return () => { cancelled = true; };
  }, []);
  // Hvilken win viser vi i WinModal? null = lukket. Sættes til win.id.
  const [activeWinId, setActiveWinId] = useState(null);
  // Hvilke wins har vi auto-poppet i denne session — undgå at åbne igen ved hver lobby-visit.
  const [autoShownWinIds, setAutoShownWinIds] = useState(new Set());
  // Claim-form draft — email + valgfri besked.
  const [claimDraft, setClaimDraft] = useState({ email: "mp@cvx.vc", message: "" });
  // Hvilken win er vi i gang med at claime? (driver ClaimView)
  const [claimingWinId, setClaimingWinId] = useState(null);
  // Efter submit viser ClaimView en success-state — vi gemmer den senest-claim'ede id her.
  const [justClaimedWinId, setJustClaimedWinId] = useState(null);

  // Auto-pop WinModal første gang brugeren rammer lobby med en uindløst præmie.
  useEffect(() => {
    if (view !== "lobby") return;
    const next = wins.find(w => w.status === "unclaimed" && !autoShownWinIds.has(w.id));
    if (next) {
      setActiveWinId(next.id);
      setAutoShownWinIds(prev => {
        const merged = new Set(prev);
        merged.add(next.id);
        return merged;
      });
    }
  }, [view, wins, autoShownWinIds]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [expandedRounds, setExpandedRounds] = useState({}); // { roundNum: true } per session
  const [selectedLeague, setSelectedLeague] = useState("ALL");
  const [selectedDate, setSelectedDate] = useState(null); // YYYY-MM-DD; null = auto-vælg første tilgængelige
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [picks, setPicks] = useState({}); // { matchId: { selections: [opt, opt, opt] } }
  const [currentLayer, setCurrentLayer] = useState(0);
  const [tempSelections, setTempSelections] = useState([]);
  const [animating, setAnimating] = useState(false);
  const [rerollsLeft, setRerollsLeft] = useState(3);
  const [rerolledMatches, setRerolledMatches] = useState({}); // { matchId: true }
  const [streak, setStreak] = useState(5);
  // XP læses live fra profileRow.xp. setXp er lokal fallback indtil profileRow loader.
  const [localXp, setLocalXp] = useState(0);
  const xp = profileRow?.xp ?? localXp;
  const setXp = setLocalXp;
  const [submitted, setSubmitted] = useState(false);
  const [countdownMs, setCountdownMs] = useState(ROUND.endsInMs);
  const [statsExpanded, setStatsExpanded] = useState(false); // persisteret per session

  // Live leaderboard for den aktive runde. Aggregerer points pr. bruger
  // (faktiske points_earned hvis runden er resolved, ellers "in-play" odds × 100
  // med 1.5× på bonus-kampe). Opdaterer også ROUND.totalPlayers / myPosition /
  // myPoints / pointsToBronze så lobby-cardet viser ægte tal.
  const [LEADERBOARD, setLEADERBOARD] = useState(FALLBACK_LEADERBOARD);
  const [leaderboardRefresh, setLeaderboardRefresh] = useState(0);
  useEffect(() => {
    if (!activeRoundId) return;
    let cancelled = false;
    const fetchLeaderboard = async () => {
      const { data, error } = await supabase
        .from("picks")
        .select(`
          user_id,
          profiles!inner(display_name, handle),
          matches!inner(is_bonus),
          pick_layers(odds, points_earned)
        `)
        .eq("round_id", activeRoundId);
      if (cancelled) return;
      if (error) { console.error("Failed to load leaderboard:", error); return; }
      const byUser = new Map();
      for (const p of (data || [])) {
        const userId = p.user_id;
        const userName = p.profiles?.display_name || p.profiles?.handle || "Anonymous";
        const isBonus = !!p.matches?.is_bonus;
        if (!byUser.has(userId)) byUser.set(userId, { userId, name: userName, points: 0 });
        for (const layer of (p.pick_layers || [])) {
          if (layer.points_earned !== null && layer.points_earned !== undefined) {
            byUser.get(userId).points += Number(layer.points_earned);
          } else {
            const inPlay = Math.floor(parseFloat(layer.odds) * 100) * (isBonus ? 1.5 : 1);
            byUser.get(userId).points += inPlay;
          }
        }
      }
      const sorted = Array.from(byUser.values()).sort((a, b) => b.points - a.points);
      const tierFor = (rank) =>
        rank === 1 ? "gold" : rank === 2 ? "silver" : rank === 3 ? "bronze" : rank <= 10 ? "topten" : null;
      const rows = sorted.map((u, idx) => ({
        rank: idx + 1,
        name: u.name,
        points: Math.round(u.points),
        tier: tierFor(idx + 1),
        userId: u.userId,
      }));
      setLEADERBOARD(rows);
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      const myRow = currentUser ? rows.find(r => r.userId === currentUser.id) : null;
      const bronzePoints = rows[2]?.points || 0;
      setROUND(prev => ({
        ...prev,
        totalPlayers: rows.length,
        myPosition: myRow?.rank || 0,
        myPoints: myRow?.points || 0,
        pointsToBronze: myRow ? Math.max(0, bronzePoints - myRow.points) : 0,
      }));
    };
    fetchLeaderboard();
    return () => { cancelled = true; };
  }, [activeRoundId, leaderboardRefresh]);

  // Hent brugerens eksisterende picks for den aktive runde, så match-kortene
  // viser hvad der allerede er gemt fra tidligere sessioner. Hydrerer både
  // `picks`-state'en (med selections) og `rerolledMatches`.
  useEffect(() => {
    if (!activeRoundId) return;
    let cancelled = false;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user || cancelled) return;
      supabase
        .from("picks")
        .select("match_id, rerolled, pick_layers(layer, option_label, option_icon, odds)")
        .eq("round_id", activeRoundId)
        .eq("user_id", user.id)
        .then(({ data, error }) => {
          if (cancelled) return;
          if (error) { console.error("Failed to load existing picks:", error); return; }
          const reconstructed = {};
          const reconstructedReroll = {};
          for (const p of (data || [])) {
            const layers = (p.pick_layers || []).slice().sort((a, b) => a.layer - b.layer);
            reconstructed[p.match_id] = {
              selections: layers.map(l => ({
                label: l.option_label,
                icon: l.option_icon,
                odds: parseFloat(l.odds),
              })),
            };
            if (p.rerolled) reconstructedReroll[p.match_id] = true;
          }
          setPicks(reconstructed);
          setRerolledMatches(reconstructedReroll);
        });
    });
    return () => { cancelled = true; };
  }, [activeRoundId]);

  // Drag-to-scroll for league filter (desktop discoverability fix — trackpad users
  // scroll vertically by reflex and miss horizontal overflow).
  const leagueScrollRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, scrollLeft: 0, hasMoved: false, captured: false, pointerId: null });

  const onLeaguePointerDown = (e) => {
    // Only hijack mouse/pen — let touch use native momentum scroll
    if (e.pointerType === "touch") return;
    const el = leagueScrollRef.current;
    if (!el) return;
    dragRef.current = {
      active: true,
      startX: e.clientX,
      scrollLeft: el.scrollLeft,
      hasMoved: false,
      captured: false,
      pointerId: e.pointerId,
    };
    // Defer setPointerCapture until we actually drag — otherwise plain clicks
    // on a pill get redirected and never fire onClick.
  };
  const onLeaguePointerMove = (e) => {
    const d = dragRef.current;
    if (!d.active) return;
    const dx = e.clientX - d.startX;
    if (!d.hasMoved && Math.abs(dx) > 4) {
      d.hasMoved = true;
      const el = leagueScrollRef.current;
      if (el && !d.captured) {
        try { el.setPointerCapture(d.pointerId); d.captured = true; } catch {}
      }
    }
    if (d.hasMoved) {
      const el = leagueScrollRef.current;
      if (el) el.scrollLeft = d.scrollLeft - dx;
    }
  };
  const onLeaguePointerUp = () => {
    const d = dragRef.current;
    const el = leagueScrollRef.current;
    if (el && d.captured && d.pointerId !== null) {
      try { if (el.hasPointerCapture(d.pointerId)) el.releasePointerCapture(d.pointerId); } catch {}
    }
    d.active = false;
    d.captured = false;
    // hasMoved stays true briefly so the synthetic click on a pill can be swallowed
    setTimeout(() => { dragRef.current.hasMoved = false; }, 0);
  };
  const handleLeagueClick = (code) => (e) => {
    if (dragRef.current.hasMoved) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    setSelectedLeague(code);
  };

  // Countdown viser tid til næste ulåste kamp (= næste kickoff i fremtiden).
  // Recomputer fra MATCHES hvert minut — når en kamp passes, skifter countdown
  // automatisk til den næste.
  useEffect(() => {
    const compute = () => {
      const now = Date.now();
      const upcoming = MATCHES
        .map(m => (m.kickoffAt ? new Date(m.kickoffAt).getTime() : null))
        .filter(t => t && t > now)
        .sort((a, b) => a - b)[0];
      return upcoming ? upcoming - now : 0;
    };
    setCountdownMs(compute());
    const t = setInterval(() => setCountdownMs(compute()), 60000);
    return () => clearInterval(t);
  }, [MATCHES]);

  const countdown = formatMs(countdownMs);
  const level = Math.floor(xp / 500) + 1;
  const levelProgress = (xp % 500) / 5;  // procent til næste level (0-100)

  // Level-up detection — vis celebration-modal kun når brugeren faktisk levler op.
  // Vent på profileRow så vi ikke trigger ved transition fra "no data" → "loaded".
  const prevLevelRef = useRef(null);
  const [levelUpShow, setLevelUpShow] = useState(null);
  useEffect(() => {
    if (!profileRow) return;
    if (prevLevelRef.current === null) {
      prevLevelRef.current = level;
      return;
    }
    if (level > prevLevelRef.current) {
      setLevelUpShow(level);
      const t = setTimeout(() => setLevelUpShow(null), 3500);
      prevLevelRef.current = level;
      return () => clearTimeout(t);
    }
    prevLevelRef.current = level;
  }, [level, profileRow]);
  const numPicked = Object.keys(picks).length;
  const canPickMore = numPicked < ROUND.maxPicks;
  // Er der mindst én kamp i runden som endnu ikke er sparket i gang?
  const anyMatchStillOpen = MATCHES.some(m => m.kickoffAt && new Date(m.kickoffAt).getTime() > Date.now());

  // Helper: lokal YYYY-MM-DD fra et ISO-timestamp.
  const dateKey = (iso) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  // Datoer der har mindst én kamp i denne runde, sorteret kronologisk.
  const availableDates = Array.from(new Set(
    MATCHES.filter(m => m.kickoffAt).map(m => dateKey(m.kickoffAt))
  )).sort();

  // Auto-vælg startdato: i dag hvis der er kampe i dag, ellers første dato i fremtiden.
  useEffect(() => {
    if (selectedDate || availableDates.length === 0) return;
    const today = new Date();
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    if (availableDates.includes(todayKey)) setSelectedDate(todayKey);
    else setSelectedDate(availableDates.find(d => d >= todayKey) || availableDates[0]);
  }, [availableDates, selectedDate]);

  // Date navigation: index i availableDates, edge-detection, og DD/MM-DA format.
  const dateIdx = availableDates.indexOf(selectedDate);
  const isFirstDate = dateIdx <= 0;
  const isLastDate = dateIdx === -1 || dateIdx >= availableDates.length - 1;
  const goToDate = (delta) => {
    const newIdx = dateIdx + delta;
    if (newIdx >= 0 && newIdx < availableDates.length) setSelectedDate(availableDates[newIdx]);
  };
  const formatDateBar = (yyyymmdd) => {
    if (!yyyymmdd) return "";
    const [y, m, d] = yyyymmdd.split("-").map(Number);
    const target = new Date(y, m - 1, d);
    const dayAbbr = target.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase().slice(0, 2);
    return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")} ${dayAbbr}`;
  };

  // Format ms-til-kickoff som "2d 14h", "14h 23m", "23m", eller "<1m".
  const formatLockCountdown = (iso) => {
    if (!iso) return "";
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return "locked";
    const totalMin = Math.floor(ms / 60000);
    const d = Math.floor(totalMin / 1440);
    const h = Math.floor((totalMin % 1440) / 60);
    const m = totalMin % 60;
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m`;
    return "<1m";
  };

  const filteredMatches = MATCHES.filter(m => {
    if (selectedLeague !== "ALL" && m.league !== selectedLeague) return false;
    if (selectedDate && m.kickoffAt && dateKey(m.kickoffAt) !== selectedDate) return false;
    return true;
  });

  const currentMatch = MATCHES.find(m => m.id === selectedMatchId);

  // Compute total points across all picks
  const totalPoints = Object.entries(picks).reduce((acc, [mId, p]) => {
    const match = MATCHES.find(m => m.id === mId);
    const matchPoints = p.selections.reduce((s, sel) => s + Math.floor(sel.odds * 100), 0);
    const multiplier = match?.isBonus ? 1.5 : 1;
    return acc + Math.floor(matchPoints * multiplier);
  }, 0);

  const handlePickMatch = (match) => {
    if (picks[match.id]) {
      // Already picked — go to review/edit
      setSelectedMatchId(match.id);
      setTempSelections(picks[match.id].selections);
      setCurrentLayer(0);
      setView("match");
    } else if (canPickMore) {
      setSelectedMatchId(match.id);
      setTempSelections([]);
      setCurrentLayer(0);
      setView("match");
    }
  };

  const handleOptionSelect = (option) => {
    if (animating) return;
    setAnimating(true);
    const newSelections = [...tempSelections.slice(0, currentLayer), option];
    setTempSelections(newSelections);
    setTimeout(() => {
      setAnimating(false);
      if (currentLayer < 2) {
        setCurrentLayer(currentLayer + 1);
      } else {
        // Save pick and return to round
        setPicks({ ...picks, [selectedMatchId]: { selections: newSelections } });
        setView("round");
        setSelectedMatchId(null);
      }
    }, 350);
  };

  const handleReroll = () => {
    if (rerollsLeft <= 0 || rerolledMatches[selectedMatchId]) return;
    if (!currentMatch?.layers[3]?.rerollOptions) return;
    setRerollsLeft(rerollsLeft - 1);
    setRerolledMatches({ ...rerolledMatches, [selectedMatchId]: true });
  };

  const handleSkipLayer3 = () => {
    // Save pick with only 2 selections — layer 3 skipped as a "safe play"
    if (tempSelections.length < 2) return;
    const twoLayerPick = tempSelections.slice(0, 2);
    setPicks({ ...picks, [selectedMatchId]: { selections: twoLayerPick } });
    setView("round");
    setSelectedMatchId(null);
  };

  const handleBackFromMatch = () => {
    setView("round");
    setSelectedMatchId(null);
    setTempSelections([]);
    setCurrentLayer(0);
  };

  const handleRemovePick = async (mId) => {
    // Optimistisk: opdater lokalt først.
    const newPicks = { ...picks };
    delete newPicks[mId];
    setPicks(newPicks);
    // Slet også fra DB. RLS afviser hvis kampen er startet.
    if (activeRoundId) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { error } = await supabase
          .from("picks")
          .delete()
          .eq("round_id", activeRoundId)
          .eq("user_id", user.id)
          .eq("match_id", mId);
        if (error) console.error("Failed to delete pick:", error);
      } catch (e) {
        console.error("Failed to delete pick:", e);
      }
    }
  };

  const handleSubmit = async () => {
    if (activeRoundId) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Not signed in");
        // Filter out picks for matches that have already kicked off — RLS
        // would block them anyway, but we want a clean UX without errors.
        const now = Date.now();
        const eligible = Object.entries(picks).filter(([matchId]) => {
          const m = MATCHES.find(x => x.id === matchId);
          return m && m.kickoffAt && new Date(m.kickoffAt).getTime() > now;
        });
        // Replace only the picks the user is re-saving — keep any other
        // previously-saved picks untouched. Matches that already started
        // can't be deleted by RLS anyway.
        const eligibleMatchIds = eligible.map(([matchId]) => matchId);
        if (eligibleMatchIds.length > 0) {
          await supabase
            .from("picks")
            .delete()
            .eq("round_id", activeRoundId)
            .eq("user_id", user.id)
            .in("match_id", eligibleMatchIds);
        }
        const pickRows = eligible.map(([matchId, p]) => ({
          round_id: activeRoundId,
          user_id: user.id,
          match_id: matchId,
          skip_locked: p.selections.length === 2,
          rerolled: !!rerolledMatches[matchId],
        }));
        if (pickRows.length === 0) throw new Error("No eligible picks to save (alle valgte kampe er allerede sparket i gang)");
        const { data: insertedPicks, error: pErr } = await supabase
          .from("picks")
          .insert(pickRows)
          .select();
        if (pErr) throw pErr;
        const layerRows = [];
        for (const ip of insertedPicks) {
          const localPick = picks[ip.match_id];
          if (!localPick) continue;
          localPick.selections.forEach((sel, idx) => {
            layerRows.push({
              pick_id: ip.id,
              layer: idx + 1,
              option_label: sel.label,
              option_icon: sel.icon || null,
              odds: sel.odds,
              result: "pending",
            });
          });
        }
        const { error: lErr } = await supabase.from("pick_layers").insert(layerRows);
        if (lErr) throw lErr;
        const { error: rpcErr } = await supabase.rpc("submit_round", { p_round_id: activeRoundId });
        if (rpcErr) throw rpcErr;
        setLeaderboardRefresh(n => n + 1);
      } catch (e) {
        console.error("Submit failed:", e);
        alert("Could not submit picks: " + (e.message || String(e)));
        return;
      }
    }
    setSubmitted(true);
    setView("submitted");
    // XP håndteres nu af submit_round-RPC'en serverside.
    // Refresh profileRow så lobby viser den nye XP-værdi.
    if (activeRoundId) refreshProfileRow();
  };

  const layer3Options = currentMatch && rerolledMatches[currentMatch.id] && currentMatch.layers[3]?.rerollOptions
    ? currentMatch.layers[3].rerollOptions
    : currentMatch?.layers[2].options;

  return (
    <div style={{
      minHeight: "100vh",
      background: "#fbf8f1",
      color: "#1c1917",
      fontFamily: "'Rajdhani', 'Barlow Condensed', sans-serif",
      position: "relative",
      overflow: "hidden",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&family=Barlow+Condensed:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }

        .soft-tint {
          position: fixed; inset: 0; pointer-events: none;
          background:
            radial-gradient(circle at 85% -5%, rgba(255,184,0,0.10) 0%, transparent 45%),
            radial-gradient(circle at -10% 110%, rgba(234,88,12,0.05) 0%, transparent 45%);
        }

        .card {
          background: #ffffff;
          border: 1px solid #ede8df;
          border-radius: 16px;
          box-shadow: 0 1px 2px rgba(120,90,0,0.04);
          transition: all 0.2s;
        }
        .card-interactive { cursor: pointer; }
        .card-interactive:hover {
          border-color: #d97706;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(217,119,6,0.14);
        }
        .card-picked {
          border-color: #fcd34d;
          background: #fffbeb;
        }
        .card-disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .bonus-glow {
          position: relative;
          border: 2px solid #ffb800 !important;
          background: linear-gradient(135deg, #fffbeb 0%, #fff 40%, #fffbeb 100%) !important;
          box-shadow: 0 0 0 1px rgba(255,184,0,0.2), 0 8px 32px rgba(255,184,0,0.18) !important;
          animation: bonusPulse 2.5s ease-in-out infinite;
        }
        @keyframes bonusPulse {
          0%, 100% { box-shadow: 0 0 0 1px rgba(255,184,0,0.2), 0 8px 32px rgba(255,184,0,0.18); }
          50%      { box-shadow: 0 0 0 2px rgba(255,184,0,0.35), 0 10px 44px rgba(255,184,0,0.32); }
        }
        .bonus-badge {
          position: absolute; top: -10px; left: 16px;
          background: linear-gradient(135deg, #ffb800, #d97706);
          color: #1c1917;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1.5px;
          box-shadow: 0 4px 12px rgba(217,119,6,0.3);
        }
        .bonus-mult {
          background: #fef3c7;
          color: #92400e;
          padding: 2px 8px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.5px;
        }

        .btn-primary {
          background: linear-gradient(135deg, #ffb800, #d97706);
          border: none;
          border-radius: 14px;
          padding: 16px 28px;
          color: #1c1917;
          font-family: 'Rajdhani', sans-serif;
          font-weight: 700;
          font-size: 16px;
          cursor: pointer;
          letter-spacing: 1px;
          text-transform: uppercase;
          box-shadow: 0 4px 16px rgba(217,119,6,0.3);
          transition: all 0.2s;
        }
        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 28px rgba(217,119,6,0.45);
        }
        .btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }

        .btn-secondary {
          background: #ffffff;
          border: 1px solid #ede8df;
          border-radius: 10px;
          padding: 8px 14px;
          color: #78716c;
          cursor: pointer;
          font-family: 'Rajdhani', sans-serif;
          font-size: 13px;
          font-weight: 600;
          transition: all 0.2s;
          box-shadow: 0 1px 2px rgba(120,90,0,0.04);
        }
        .btn-secondary:hover {
          background: #fffbf0;
          color: #1c1917;
          border-color: #d97706;
        }

        .btn-reroll {
          background: linear-gradient(135deg, #fef3c7, #fed7aa);
          border: 1px solid #f59e0b;
          border-radius: 10px;
          padding: 8px 14px;
          color: #92400e;
          cursor: pointer;
          font-family: 'Rajdhani', sans-serif;
          font-size: 13px;
          font-weight: 700;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .btn-reroll:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(245,158,11,0.25);
        }
        .btn-reroll:disabled { opacity: 0.4; cursor: not-allowed; }

        .option-btn {
          background: #ffffff;
          border: 1px solid #ede8df;
          border-radius: 14px;
          padding: 16px 18px;
          cursor: pointer;
          transition: all 0.2s;
          color: #1c1917;
          text-align: left;
          width: 100%;
          position: relative;
          overflow: hidden;
          box-shadow: 0 1px 2px rgba(120,90,0,0.04);
        }
        .option-btn::after {
          content: ''; position: absolute; left: 0; top: 0; bottom: 0;
          width: 3px;
          background: linear-gradient(180deg, #ffb800, #d97706);
          transform: scaleY(0);
          transition: transform 0.2s;
          border-radius: 3px 0 0 3px;
        }
        .option-btn:hover {
          background: #fffbf0; border-color: #d97706;
          transform: translateX(3px);
          box-shadow: 0 4px 16px rgba(217,119,6,0.14);
        }
        .option-btn:hover::after { transform: scaleY(1); }
        .option-btn.risky {
          background: linear-gradient(135deg, #fff, #fffbeb);
          border-color: #fcd34d;
        }
        .option-btn.risky:hover {
          background: linear-gradient(135deg, #fffbf0, #fef3c7);
        }

        .chip {
          background: #fef6e0;
          border: 1px solid #fcd34d;
          border-radius: 8px;
          padding: 5px 10px;
          font-size: 12px;
          color: #92400e;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-weight: 600;
        }

        .progress-bar { height: 4px; background: #ede8df; border-radius: 4px; overflow: hidden; }
        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #ffb800, #d97706);
          border-radius: 4px;
          transition: width 0.5s;
        }

        .slide-in { animation: slideIn 0.3s ease forwards; }
        @keyframes slideIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }

        @keyframes trophyIn {
          0%   { opacity: 0; transform: scale(0.4) translateY(40px); }
          60%  { opacity: 1; transform: scale(1.08) translateY(0); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes badgeGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,184,0,0.0); }
          50%      { box-shadow: 0 0 36px 8px rgba(255,184,0,0.55); }
        }
        @keyframes backdropFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes modalRise {
          from { opacity: 0; transform: translateY(24px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }

        .league-pill {
          background: #ffffff;
          border: 1px solid #ede8df;
          border-radius: 999px;
          padding: 8px 14px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          color: #78716c;
          transition: all 0.15s;
          white-space: nowrap;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .league-pill.active {
          background: linear-gradient(135deg, #ffb800, #d97706);
          color: #1c1917;
          border-color: transparent;
          box-shadow: 0 4px 12px rgba(217,119,6,0.22);
        }

        .scroll-x {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          scrollbar-width: none;
          padding: 2px 0;
        }
        .scroll-x::-webkit-scrollbar { display: none; }

        /* Drag-to-scroll variant: discoverable on desktop where trackpad
           reflexes scroll vertically and miss horizontal overflow. */
        .scroll-x-drag {
          cursor: grab;
          user-select: none;
          -webkit-user-select: none;
          touch-action: pan-x;
        }
        .scroll-x-drag:active { cursor: grabbing; }
        .scroll-x-drag .league-pill { user-select: none; }

        .position-badge {
          background: linear-gradient(135deg, #ffb800, #d97706);
          color: #1c1917;
          border-radius: 8px;
          padding: 4px 10px;
          font-weight: 700;
          font-size: 13px;
          letter-spacing: 0.5px;
        }
      `}</style>

      <div className="soft-tint" />

      <div style={{ maxWidth: 480, margin: "0 auto", padding: "0 16px 110px", position: "relative" }}>

        {/* ==================== TOP HEADER ==================== */}
        <div style={{ padding: "20px 0 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <svg width="22" height="30" viewBox="130 55 110 150" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
                <polygon points="185,205 130,150 155,55 185,95 215,55 240,150" fill="#b45309" />
                <circle cx="162" cy="135" r="5" fill="#fbf8f1" />
                <circle cx="208" cy="135" r="5" fill="#fbf8f1" />
              </svg>
              <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: -0.3 }}>
                <span style={{ color: "#44403c" }}>pick</span>
                <span style={{ color: "#b45309" }}>fox</span>
              </div>
            </div>
            <div style={{ fontSize: 11, color: "#a8a29e", letterSpacing: 1, marginTop: 2 }}>ROUND {ROUND.number}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 10, color: "#a8a29e", letterSpacing: 1 }}>LEVEL</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#b45309" }}>{level}</div>
            </div>
            <div style={{ background: "linear-gradient(135deg,#ffb800,#d97706)", borderRadius: 10, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14, color: "#1c1917", boxShadow: "0 3px 10px rgba(217,119,6,0.25)" }}>
              {level}
            </div>
          </div>
        </div>

        {/* XP bar */}
        <div className="progress-bar" style={{ marginBottom: 20 }}>
          <div className="progress-fill" style={{ width: `${levelProgress}%` }} />
        </div>

        {/* ==================== LOBBY VIEW ==================== */}
        {view === "lobby" && (
          <div className="slide-in">
            {submitted && (
              <div style={{ background: "#ecfdf5", border: "1px solid #6ee7b7", borderRadius: 14, padding: "14px 18px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 22 }}>✅</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#047857" }}>Round submitted!</div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>Awaiting match results…</div>
                </div>
              </div>
            )}

            {/* Win-banner — vises hvis der er en eller flere uindløste præmier */}
            {wins.filter(w => w.status === "unclaimed").map(win => {
              const tier = tierById(win.tier);
              return (
                <div
                  key={win.id}
                  onClick={() => setActiveWinId(win.id)}
                  style={{
                    background: "linear-gradient(135deg, #fff7ed, #fef3c7)",
                    border: "1px solid #fbbf24",
                    borderRadius: 14,
                    padding: "14px 16px",
                    marginBottom: 12,
                    display: "flex", alignItems: "center", gap: 14,
                    cursor: "pointer",
                    boxShadow: "0 4px 14px rgba(217,119,6,0.14)",
                  }}
                >
                  <div style={{
                    width: 44, height: 44, borderRadius: 22,
                    background: tier.bg, color: tier.fg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 18, fontWeight: 700, flexShrink: 0,
                  }}>{tier.letter}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, letterSpacing: 1.2, color: "#92400e", fontWeight: 700 }}>YOU WON {tier.name.toUpperCase()} · ROUND {win.roundNumber}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#1c1917", marginTop: 2 }}>{win.prizeLabel}</div>
                  </div>
                  <div style={{ fontSize: 22, color: "#b45309" }}>›</div>
                </div>
              );
            })}

            {/* Round hero card */}
            <div className="card" style={{ padding: 22, marginBottom: 16, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 100% 0%, rgba(255,184,0,0.12), transparent 55%)", pointerEvents: "none" }} />

              <div style={{ position: "relative" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: "#a8a29e", fontWeight: 600 }}>ACTIVE ROUND</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#ea580c", fontWeight: 700 }}>
                    <span>⏱</span>
                    <span>{countdown.d}d {countdown.h}h {countdown.m}m</span>
                  </div>
                </div>

                <div style={{ background: "#1c1917", borderRadius: 14, padding: "14px 16px", marginBottom: 18 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div style={{ fontSize: 10, letterSpacing: 1.4, color: "#fbbf24", fontWeight: 600 }}>WIN THIS ROUND · #1</div>
                    {SPONSORS[headlineTier.sponsorId].logo({ inverted: true })}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "#fafaf9", lineHeight: 1.15, marginBottom: 2 }}>{headlineTier.prize}</div>
                  <div style={{ fontSize: 11, color: "#a8a29e" }}>{headlineTier.prizeDetail}</div>
                </div>

                <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                  <div style={{ flex: 1, background: "#faf7ef", border: "1px solid #f5f1e8", borderRadius: 10, padding: "10px 12px" }}>
                    <div style={{ fontSize: 10, color: "#a8a29e", letterSpacing: 1 }}>MY RANK</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#1c1917" }}>#{ROUND.myPosition}</div>
                  </div>
                  <div style={{ flex: 1, background: "#faf7ef", border: "1px solid #f5f1e8", borderRadius: 10, padding: "10px 12px" }}>
                    <div style={{ fontSize: 10, color: "#a8a29e", letterSpacing: 1 }}>STREAK 🔥</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#ea580c" }}>{streak}</div>
                  </div>
                  <div style={{ flex: 1, background: "#faf7ef", border: "1px solid #f5f1e8", borderRadius: 10, padding: "10px 12px" }}>
                    <div style={{ fontSize: 10, color: "#a8a29e", letterSpacing: 1 }}>XP</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#1c1917" }}>{xp.toLocaleString("en-US")}</div>
                  </div>
                </div>

                <button className="btn-primary" style={{ width: "100%" }} onClick={() => { setView("round"); }}>
                  {numPicked > 0
                    ? (anyMatchStillOpen ? `Edit picks (${numPicked}/${ROUND.maxPicks})` : "View picks")
                    : `Start Round ${ROUND.number} →`}
                </button>
              </div>
            </div>

            {/* Leaderboard preview */}
            <div className="card card-interactive" style={{ padding: 18, marginBottom: 14 }} onClick={() => setView("leaderboard")}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 12, letterSpacing: 1.5, color: "#a8a29e", fontWeight: 700 }}>LEADERBOARD — TOP 3</div>
                <div style={{ fontSize: 12, color: "#b45309", fontWeight: 600 }}>See all →</div>
              </div>
              {LEADERBOARD.length === 0 ? (
                <div style={{ padding: "16px 0", textAlign: "center", color: "#a8a29e", fontSize: 13 }}>
                  No picks yet — be the first to play.
                </div>
              ) : LEADERBOARD.slice(0, 3).map((row, idx) => {
                const tier = tierById(row.tier) || { bg: "#f5f1e8", fg: "#78716c", labelColor: "#a8a29e", name: "—" };
                return (
                  <div key={row.rank} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: idx < 2 ? "1px solid #f5f1e8" : "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 26, height: 26, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700,
                        background: tier.bg, color: tier.fg }}>
                        {row.rank}
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{row.name}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ fontSize: 13, color: "#78716c", fontWeight: 600 }}>{row.points} pt</div>
                      <div style={{ fontSize: 11, color: tier.labelColor, fontWeight: 700, letterSpacing: 1.2 }}>{tier.name.toUpperCase()}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Rerolls remaining hint */}
            <div className="card" style={{ padding: 14, display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 26 }}>🎲</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>Rerolls this round</div>
                <div style={{ fontSize: 11, color: "#a8a29e" }}>Use on Layer 3 to swap to riskier props</div>
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#b45309" }}>{rerollsLeft}/3</div>
            </div>

          </div>
        )}

        {/* ==================== ROUND VIEW — pick matches ==================== */}
        {view === "round" && (
          <div className="slide-in">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <button className="btn-secondary" onClick={() => setView("lobby")}>← Home</button>
              <div>
                <div style={{ fontSize: 11, color: "#a8a29e", letterSpacing: 1, fontWeight: 600 }}>PICK MATCHES</div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>Round {ROUND.number} · {numPicked}/{ROUND.maxPicks} picked</div>
              </div>
            </div>

            {/* Date navigator — Flashscore-stil bar med pile */}
            {availableDates.length > 0 && (
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: "#fbf8f1", border: "1px solid #ece6d8", borderRadius: 10,
                padding: "4px 6px", marginBottom: 10,
              }}>
                <button
                  onClick={() => goToDate(-1)}
                  disabled={isFirstDate}
                  aria-label="Previous date"
                  style={{
                    background: "transparent", border: "none", padding: "6px 12px",
                    cursor: isFirstDate ? "default" : "pointer",
                    color: isFirstDate ? "#d6cfbf" : "#57534e",
                    fontSize: 18, fontWeight: 600, lineHeight: 1,
                  }}
                >‹</button>
                <div style={{
                  display: "flex", alignItems: "center", gap: 8,
                  fontSize: 13, fontWeight: 600, color: "#1c1917", letterSpacing: 0.4,
                }}>
                  <span style={{ fontSize: 14 }}>📅</span>
                  <span>{formatDateBar(selectedDate)}</span>
                </div>
                <button
                  onClick={() => goToDate(1)}
                  disabled={isLastDate}
                  aria-label="Next date"
                  style={{
                    background: "transparent", border: "none", padding: "6px 12px",
                    cursor: isLastDate ? "default" : "pointer",
                    color: isLastDate ? "#d6cfbf" : "#57534e",
                    fontSize: 18, fontWeight: 600, lineHeight: 1,
                  }}
                >›</button>
              </div>
            )}

            {/* League filter — drag-to-scroll on desktop, native swipe on touch */}
            <div
              ref={leagueScrollRef}
              className="scroll-x scroll-x-drag"
              style={{ marginBottom: 16 }}
              onPointerDown={onLeaguePointerDown}
              onPointerMove={onLeaguePointerMove}
              onPointerUp={onLeaguePointerUp}
              onPointerCancel={onLeaguePointerUp}
            >
              {LEAGUES.map(lg => (
                <div key={lg.code} className={`league-pill ${selectedLeague === lg.code ? "active" : ""}`} onClick={handleLeagueClick(lg.code)}>
                  <span>{lg.flag}</span>
                  <span>{lg.name}</span>
                </div>
              ))}
            </div>

            {/* Match list */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {filteredMatches.map(match => {
                const isPicked = !!picks[match.id];
                const isLocked = match.kickoffAt && new Date(match.kickoffAt).getTime() <= Date.now();
                const isDisabled = isLocked || (!isPicked && !canPickMore);
                const cardClass = `card card-interactive ${match.isBonus ? "bonus-glow" : ""} ${isPicked ? "card-picked" : ""} ${isDisabled ? "card-disabled" : ""}`;
                return (
                  <div key={match.id} className={cardClass} style={{ padding: 18, position: "relative" }} onClick={() => !isDisabled && handlePickMatch(match)}>
                    {match.isBonus && (
                      <div className="bonus-badge">⭐ MATCH OF THE ROUND</div>
                    )}

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 16 }}>{LEAGUES.find(l => l.code === match.league)?.flag}</span>
                        <div>
                          <div style={{ fontSize: 11, color: "#a8a29e", letterSpacing: 1, fontWeight: 600 }}>{LEAGUES.find(l => l.code === match.league)?.name}</div>
                          <div style={{ fontSize: 12, color: "#78716c" }}>{match.date} · {match.time} · {isLocked ? "🔒 Locked" : <span style={{ color: "#b45309", fontWeight: 600 }}>locks in {formatLockCountdown(match.kickoffAt)}</span>}</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {match.isBonus && <div className="bonus-mult">1.5× POINTS</div>}
                        {isPicked && (
                          <div style={{ background: "#059669", color: "#fff", borderRadius: 999, width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>✓</div>
                        )}
                      </div>
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ flex: 1, textAlign: "center" }}>
                        <div style={{ fontWeight: 700, fontSize: 18, color: "#1c1917" }}>{match.homeShort}</div>
                        <div style={{ fontSize: 10, color: "#a8a29e" }}>{match.home}</div>
                      </div>
                      <div style={{ padding: "4px 12px", fontSize: 12, fontWeight: 600, color: "#d6d3d1", letterSpacing: 1 }}>VS</div>
                      <div style={{ flex: 1, textAlign: "center" }}>
                        <div style={{ fontWeight: 700, fontSize: 18, color: "#1c1917" }}>{match.awayShort}</div>
                        <div style={{ fontSize: 10, color: "#a8a29e" }}>{match.away}</div>
                      </div>
                    </div>

                    {isPicked && (() => {
                      const layerCount = picks[match.id].selections.length; // 2 or 3
                      const matchRawPts = picks[match.id].selections.reduce((s, sel) => s + Math.floor(sel.odds * 100), 0);
                      const matchFinalPts = match.isBonus ? Math.floor(matchRawPts * 1.5) : matchRawPts;
                      return (
                        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #f5f1e8" }}>
                          {/* Progress pills — 3 layers, filled or dashed-skipped */}
                          <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
                            {[0, 1, 2].map(i => {
                              const filled = i < layerCount;
                              const skipped = i === 2 && layerCount === 2;
                              return (
                                <div key={i} style={{
                                  flex: 1,
                                  height: 6,
                                  borderRadius: 3,
                                  background: filled ? "linear-gradient(90deg, #ffb800, #d97706)" : "transparent",
                                  border: skipped ? "1px dashed #d6d3d1" : filled ? "none" : "1px solid #ede8df",
                                }} />
                              );
                            })}
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div style={{ fontSize: 11, color: "#059669", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                              <span>{layerCount}/3 layers</span>
                              <span style={{ color: "#d6d3d1" }}>·</span>
                              <span>{matchFinalPts} pt at stake{match.isBonus ? " (1.5×)" : ""}</span>
                            </div>
                            <button className="btn-secondary" style={{ padding: "4px 10px", fontSize: 11 }} onClick={(e) => { e.stopPropagation(); handleRemovePick(match.id); }}>Remove</button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>

            {/* Submit bar — inline nederst i round-view, ikke flydende */}
            {numPicked > 0 && (
              <div style={{ marginTop: 20 }}>
                <div className="card" style={{ padding: 12, display: "flex", alignItems: "center", gap: 10, background: "#ffffff", boxShadow: "0 4px 14px rgba(120,90,0,0.08)" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: "#a8a29e", letterSpacing: 1 }}>POSSIBLE POINTS · {numPicked}/{ROUND.maxPicks} matches</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#b45309" }}>{totalPoints}</div>
                  </div>
                  <button className="btn-primary" style={{ padding: "12px 20px", fontSize: 14 }} onClick={() => setView("confirm")}>Submit picks →</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==================== MATCH VIEW — 3-layer prediction ==================== */}
        {view === "match" && currentMatch && (
          <div className="slide-in">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
              <button className="btn-secondary" onClick={handleBackFromMatch}>← Back</button>
              <div>
                <div style={{ fontSize: 11, color: "#a8a29e", letterSpacing: 1, fontWeight: 600 }}>{LEAGUES.find(l => l.code === currentMatch.league)?.name}</div>
                <div style={{ fontWeight: 700, fontSize: 16, display: "flex", alignItems: "center", gap: 6 }}>
                  {currentMatch.homeShort} vs {currentMatch.awayShort}
                  {currentMatch.isBonus && <span className="bonus-mult">1.5×</span>}
                </div>
              </div>
            </div>

            {/* ---- Vis statistik accordion (opt-in, hidden by default) ---- */}
            {currentMatch.stats && (
              <div style={{ marginBottom: 18 }}>
                <button
                  onClick={() => setStatsExpanded(s => !s)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "11px 14px",
                    background: statsExpanded ? "#f5efe1" : "#fbf8f1",
                    border: "0.5px solid #d6cdb6",
                    borderRadius: 10,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "background 0.15s",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", flexShrink: 0 }}>
                      <rect x="2" y="9" width="2.5" height="5" rx="0.5" fill="#b45309" />
                      <rect x="6.75" y="6" width="2.5" height="8" rx="0.5" fill="#b45309" />
                      <rect x="11.5" y="3" width="2.5" height="11" rx="0.5" fill="#b45309" />
                    </svg>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#44403c" }}>
                      {statsExpanded ? "Hide stats" : "Show stats"}
                    </span>
                    {!statsExpanded && (
                      <span style={{ fontSize: 11, color: "#a8a29e" }}>form · table · h2h</span>
                    )}
                  </div>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ display: "block", transition: "transform 0.2s", transform: statsExpanded ? "rotate(180deg)" : "rotate(0deg)" }}
                  >
                    <path d="M3.5 5.25 L7 8.75 L10.5 5.25" stroke="#78716c" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                {statsExpanded && (() => {
                  const stats = currentMatch.stats;
                  const formColor = (r) => r === "V" ? "#16a34a" : r === "U" ? "#d6d3d1" : "#dc2626";
                  const formText = (r) => r === "U" ? "#44403c" : "#fff";
                  return (
                    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                      {/* Form card */}
                      <div style={{ background: "#fbf8f1", border: "0.5px solid #ece5d2", borderRadius: 10, padding: "12px 14px" }}>
                        <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#a8a29e", marginBottom: 8, fontWeight: 600 }}>FORM · LAST 5</div>
                        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                          <div style={{ fontSize: 12, color: "#44403c", width: 44, fontWeight: 600 }}>{currentMatch.homeShort}</div>
                          <div style={{ display: "flex", gap: 4 }}>
                            {stats.homeForm.map((r, i) => (
                              <div key={i} style={{ width: 18, height: 18, borderRadius: 4, background: formColor(r), color: formText(r), fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{r === "V" ? "W" : r === "U" ? "D" : "L"}</div>
                            ))}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 6 }}>
                          <div style={{ fontSize: 12, color: "#44403c", width: 44, fontWeight: 600 }}>{currentMatch.awayShort}</div>
                          <div style={{ display: "flex", gap: 4 }}>
                            {stats.awayForm.map((r, i) => (
                              <div key={i} style={{ width: 18, height: 18, borderRadius: 4, background: formColor(r), color: formText(r), fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{r === "V" ? "W" : r === "U" ? "D" : "L"}</div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Table + H2H side by side */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                        <div style={{ background: "#fbf8f1", border: "0.5px solid #ece5d2", borderRadius: 10, padding: "12px 14px" }}>
                          <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#a8a29e", marginBottom: 8, fontWeight: 600 }}>TABLE</div>
                          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                            <div style={{ fontSize: 22, fontWeight: 700, color: "#44403c" }}>{stats.homeTable.pos}.</div>
                            <div style={{ fontSize: 11, color: "#78716c" }}>{currentMatch.homeShort} · {stats.homeTable.points}p</div>
                          </div>
                          <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 4 }}>
                            <div style={{ fontSize: 22, fontWeight: 700, color: "#44403c" }}>{stats.awayTable.pos}.</div>
                            <div style={{ fontSize: 11, color: "#78716c" }}>{currentMatch.awayShort} · {stats.awayTable.points}p</div>
                          </div>
                        </div>
                        <div style={{ background: "#fbf8f1", border: "0.5px solid #ece5d2", borderRadius: 10, padding: "12px 14px" }}>
                          <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#a8a29e", marginBottom: 8, fontWeight: 600 }}>HEAD-TO-HEAD · {stats.h2hLabel}</div>
                          <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
                            {stats.h2h.map((r, i) => (
                              <div key={i} style={{ width: 16, height: 16, borderRadius: 4, background: formColor(r) }} />
                            ))}
                          </div>
                          <div style={{ fontSize: 11, color: "#78716c" }}>
                            {stats.h2h.filter(r => r === "V").length}W · {stats.h2h.filter(r => r === "U").length}D · {stats.h2h.filter(r => r === "T").length}L
                          </div>
                          <div style={{ fontSize: 10, color: "#a8a29e", marginTop: 4, lineHeight: 1.3 }}>
                            from {currentMatch.homeShort}'s perspective
                          </div>
                        </div>
                      </div>

                      <div style={{ fontSize: 10, color: "#a8a29e", textAlign: "center", marginTop: 2, lineHeight: 1.4 }}>
                        Stats fra football-data.org · brug dem som du vil
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Layer progress */}
            <div style={{ marginBottom: 22 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: "#78716c", fontWeight: 600, letterSpacing: 1 }}>LAYER {currentLayer + 1} OF 3</div>
                {tempSelections.length > 0 && (
                  <div style={{ fontSize: 13 }}>
                    <span style={{ color: "#a8a29e" }}>Total: </span>
                    <span style={{ fontWeight: 700, color: "#b45309" }}>
                      {tempSelections.reduce((s, sel) => s + Math.floor(sel.odds * 100), 0)} pt
                    </span>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{ flex: 1, height: 4, borderRadius: 4, background: i <= currentLayer ? (i < currentLayer ? "#d97706" : "#fcd34d") : "#ede8df" }} />
                ))}
              </div>
              {tempSelections.length > 0 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {tempSelections.map((s, i) => (
                    <div key={i} className="chip">
                      <span>{s.icon}</span>
                      <span>{s.label}</span>
                      <span style={{ opacity: 0.6 }}>+{Math.floor(s.odds * 100)}pt</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Layer label + reroll button */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 8 }}>
              <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: 0.5, flex: 1, minWidth: 0 }}>
                {currentLayer < 2 ? currentMatch.layers[currentLayer].label : currentMatch.layers[2].label}
              </div>
              {currentLayer === 2 && !rerolledMatches[currentMatch.id] && rerollsLeft > 0 && currentMatch.layers[3]?.rerollOptions && (
                <button className="btn-reroll" onClick={handleReroll}>
                  🎲 Reroll ({rerollsLeft})
                </button>
              )}
              {currentLayer === 2 && rerolledMatches[currentMatch.id] && (
                <div style={{ fontSize: 11, color: "#92400e", fontWeight: 700, display: "flex", alignItems: "center", gap: 4, background: "#fef3c7", padding: "4px 8px", borderRadius: 6 }}>
                  🎲 SWAPPED
                </div>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#a8a29e", marginBottom: 16 }}>
              {currentLayer === 0
                ? "Pick base result"
                : currentLayer === 1
                  ? "Pick a stat prediction"
                  : rerolledMatches[currentMatch.id]
                    ? "New risky props — pick one or lock safe below"
                    : "Risky props — high risk, high points. Or lock safe below"}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {(currentLayer === 2 ? layer3Options : currentMatch.layers[currentLayer].options).map((option, i) => {
                const isRisky = currentLayer === 2;
                // For Lag 3, show the stake math: bonus on hit, fixed -100 on miss.
                // For Lag 1/2, show per-layer points the legacy way (these layers form the parlay base).
                const bonusOnHit = isRisky ? Math.floor(100 * (option.odds - 1)) : Math.floor(option.odds * 100);
                return (
                  <button key={i} className={`option-btn ${isRisky ? "risky" : ""}`} onClick={() => handleOptionSelect(option)} style={{ animationDelay: `${i * 0.04}s` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <span style={{ fontSize: 22, minWidth: 28 }}>{option.icon}</span>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 15 }}>{option.label}</div>
                          <div style={{ fontSize: 11, color: "#a8a29e", marginTop: 2, fontWeight: 600 }}>
                            {isRisky
                              ? <>+{bonusOnHit} if hit <span style={{ color: "#b91c1c", fontSize: 10, fontWeight: 500, opacity: 0.75 }}>· −100 if miss</span></>
                              : <>+{bonusOnHit} points if correct</>}
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: 700, fontSize: 18, color: isRisky ? "#f59e0b" : "#b45309" }}>{option.odds}x</div>
                        <div style={{ fontSize: 10, color: "#a8a29e" }}>odds</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* ---- Inline filled skip-bar — only on layer 3, sits directly below options ---- */}
            {currentLayer === 2 && tempSelections.length >= 2 && (
              <div style={{ marginTop: 20, position: "relative" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{ flex: 1, height: 1, background: "#ede8df" }} />
                  <div style={{ fontSize: 10, color: "#a8a29e", letterSpacing: 2, fontWeight: 700 }}>OR</div>
                  <div style={{ flex: 1, height: 1, background: "#ede8df" }} />
                </div>
                <button
                  onClick={handleSkipLayer3}
                  style={{
                    width: "100%",
                    background: "linear-gradient(135deg, #fcd34d 0%, #f59e0b 100%)",
                    border: "none",
                    borderRadius: 14,
                    padding: "14px 18px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontFamily: "'Rajdhani', sans-serif",
                    fontWeight: 700,
                    color: "#1c1917",
                    boxShadow: "0 8px 22px rgba(217,119,6,0.28), inset 0 1px 0 rgba(255,255,255,0.4)",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 28px rgba(217,119,6,0.38), inset 0 1px 0 rgba(255,255,255,0.5)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 8px 22px rgba(217,119,6,0.28), inset 0 1px 0 rgba(255,255,255,0.4)"; }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ background: "rgba(255,255,255,0.65)", borderRadius: 10, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
                      🛡️
                    </div>
                    <div style={{ textAlign: "left" }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#1c1917", letterSpacing: 1, textTransform: "uppercase" }}>Skip & lock 2/3</div>
                      <div style={{ fontSize: 11, color: "#78350f", fontWeight: 600, marginTop: 1 }}>
                        {tempSelections.length >= 2
                          ? Math.floor(tempSelections[0].odds * tempSelections[1].odds * 100)
                          : 0} pt secured — no risky
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: 20, color: "#78350f", fontWeight: 700 }}>→</div>
                </button>
              </div>
            )}
          </div>
        )}

        {/* ==================== CONFIRM VIEW ==================== */}
        {view === "confirm" && (
          <div className="slide-in">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
              <button className="btn-secondary" onClick={() => setView("round")}>← Edit</button>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Confirm round {ROUND.number}</div>
            </div>

            <div className="card" style={{ padding: 20, marginBottom: 16, background: "linear-gradient(135deg, #fffbeb 0%, #fff 60%)", border: "1px solid #fcd34d" }}>
              <div style={{ textAlign: "center", marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: "#78716c", letterSpacing: 2, fontWeight: 600 }}>POSSIBLE POINTS</div>
                <div style={{ fontSize: 40, fontWeight: 700, color: "#b45309", letterSpacing: 0.5, lineHeight: 1.1 }}>{totalPoints}</div>
                <div style={{ fontSize: 11, color: "#a8a29e", marginTop: 4 }}>
                  Across {Object.keys(picks).length} match{Object.keys(picks).length !== 1 ? "es" : ""}
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {Object.entries(picks).map(([mId, p]) => {
                  const match = MATCHES.find(m => m.id === mId);
                  const matchPoints = p.selections.reduce((s, sel) => s + Math.floor(sel.odds * 100), 0);
                  const finalPoints = match.isBonus ? Math.floor(matchPoints * 1.5) : matchPoints;
                  const layerCount = p.selections.length;
                  const wasSkipped = layerCount === 2;
                  const isLockedMatch = match.kickoffAt && new Date(match.kickoffAt).getTime() <= Date.now();
                  return (
                    <div key={mId} style={{ padding: "10px 12px", background: "#faf7ef", borderRadius: 10, border: match.isBonus ? "1px solid #fcd34d" : "1px solid #f5f1e8" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                          {match.homeShort} vs {match.awayShort}
                          {match.isBonus && <span className="bonus-mult">⭐ 1.5×</span>}
                          {wasSkipped && <span style={{ background: "#f5f1e8", color: "#78716c", padding: "2px 6px", borderRadius: 4, fontSize: 9, fontWeight: 700, letterSpacing: 1 }}>SAFE</span>}
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#b45309" }}>+{finalPoints} pt</div>
                      </div>

                      {/* Mini progress pills */}
                      <div style={{ display: "flex", gap: 3, marginBottom: 6 }}>
                        {[0, 1, 2].map(i => {
                          const filled = i < layerCount;
                          const skipped = i === 2 && layerCount === 2;
                          return (
                            <div key={i} style={{
                              flex: 1,
                              height: 4,
                              borderRadius: 2,
                              background: filled ? "linear-gradient(90deg, #ffb800, #d97706)" : "transparent",
                              border: skipped ? "1px dashed #d6d3d1" : filled ? "none" : "1px solid #e7e2d6",
                            }} />
                          );
                        })}
                      </div>

                      <div style={{ fontSize: 11, color: "#78716c" }}>
                        {layerCount}/3 layers · {p.selections.map(s => s.label).join(" · ")}
                      </div>

                      {/* Per-pick controls — kun før kampen starter */}
                      {!isLockedMatch ? (
                        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                          <button
                            onClick={() => handlePickMatch(match)}
                            style={{ flex: 1, padding: "6px 10px", fontSize: 11, fontWeight: 600, background: "#fbf8f1", border: "1px solid #ece6d8", borderRadius: 6, color: "#57534e", cursor: "pointer", fontFamily: "inherit" }}
                          >Edit</button>
                          <button
                            onClick={() => handleRemovePick(mId)}
                            style={{ flex: 1, padding: "6px 10px", fontSize: 11, fontWeight: 600, background: "transparent", border: "1px solid #fecaca", borderRadius: 6, color: "#dc2626", cursor: "pointer", fontFamily: "inherit" }}
                          >Remove</button>
                        </div>
                      ) : (
                        <div style={{ marginTop: 8, fontSize: 11, color: "#a8a29e", textAlign: "center", fontWeight: 600 }}>🔒 Locked — match in progress</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="card" style={{ padding: 14, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 22 }}>🏆</span>
              <div style={{ fontSize: 13, color: "#78716c" }}>
                Top 10% average this round is <span style={{ color: "#1c1917", fontWeight: 700 }}>~4,200 pt</span>. You need to hit similar to share the pool.
              </div>
            </div>

            <button className="btn-primary" style={{ width: "100%" }} onClick={handleSubmit}>
              Submit picks
            </button>
            <div style={{ textAlign: "center", marginTop: 10, fontSize: 11, color: "#a8a29e" }}>
              You can still edit a pick until its match starts
            </div>
          </div>
        )}

        {/* ==================== LEADERBOARD VIEW ==================== */}
        {view === "leaderboard" && (
          <div className="slide-in">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <button className="btn-secondary" onClick={() => setView("lobby")}>← Home</button>
              <div>
                <div style={{ fontSize: 11, color: "#a8a29e", letterSpacing: 1, fontWeight: 600 }}>LEADERBOARD</div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>Round {ROUND.number} · top 10% wins</div>
              </div>
            </div>

            {/* Hero prize card */}
            <div style={{ background: "#1c1917", borderRadius: 16, padding: 16, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div style={{ fontSize: 10, letterSpacing: 1.4, color: "#fbbf24", fontWeight: 600 }}>THIS ROUND · #1 WINNER</div>
                {SPONSORS[headlineTier.sponsorId].logo({ inverted: true })}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#fafaf9", lineHeight: 1.2, marginBottom: 4 }}>{headlineTier.prize}</div>
              <div style={{ fontSize: 12, color: "#a8a29e" }}>{headlineTier.prizeDetail}</div>
            </div>

            {/* Prize tiers */}
            <div className="card" style={{ padding: "12px 14px", marginBottom: 12 }}>
              <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#a8a29e", fontWeight: 600, marginBottom: 8 }}>PRIZE TIERS</div>
              {PRIZE_TIERS.map((tier, idx) => (
                <div key={tier.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: idx < PRIZE_TIERS.length - 1 ? "1px solid #f5f1e8" : "none" }}>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: tier.bg, color: tier.fg, border: tier.id === "topten" ? "1px solid #ede8df" : "none", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>
                    {tier.letter}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#1c1917" }}>{tier.name} · {tier.range}</div>
                    <div style={{ fontSize: 11, color: "#78716c" }}>
                      {tier.prize}{tier.sponsorId ? ` · ${SPONSORS[tier.sponsorId].name}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Top 10 list */}
            <div className="card" style={{ padding: 0, marginBottom: 12, overflow: "hidden" }}>
              {LEADERBOARD.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", color: "#a8a29e", fontSize: 13 }}>
                  No picks yet — be the first to play.
                </div>
              ) : LEADERBOARD.map((row, idx) => {
                const tier = tierById(row.tier) || { bg: "#f5f1e8", fg: "#78716c", labelColor: "#a8a29e", name: "—" };
                const hasProfile = !!PLAYER_DATA[row.name];
                return (
                  <div
                    key={row.rank}
                    onClick={() => {
                      if (hasProfile) {
                        setSelectedPlayer(row.name);
                        setExpandedRounds({ 23: true });
                        setView("player");
                      }
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "12px 16px",
                      borderBottom: idx < LEADERBOARD.length - 1 ? "1px solid #f5f1e8" : "none",
                      cursor: hasProfile ? "pointer" : "default",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => { if (hasProfile) e.currentTarget.style.background = "#faf7ef"; }}
                    onMouseLeave={(e) => { if (hasProfile) e.currentTarget.style.background = "transparent"; }}
                  >
                    <div style={{ width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, marginRight: 12,
                      background: tier.bg, color: tier.fg }}>
                      {row.rank}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{row.name}</div>
                      <div style={{ fontSize: 11, color: "#a8a29e" }}>{row.points} points</div>
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: tier.labelColor, letterSpacing: 1.2, marginRight: 10 }}>
                      {tier.name.toUpperCase()}
                    </div>
                    {hasProfile && (
                      <svg width="8" height="14" viewBox="0 0 8 14" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", flexShrink: 0 }}>
                        <path d="M1 1 L7 7 L1 13" stroke="#a8a29e" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                );
              })}
            </div>

            {/* User's position */}
            <div className="card" style={{ padding: 14, background: "linear-gradient(135deg, #fff 0%, #fffbeb 100%)", border: "1px solid #fcd34d", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div className="position-badge">YOU</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>#{ROUND.myPosition} of {ROUND.totalPlayers.toLocaleString("en-US")}</div>
                  <div style={{ fontSize: 11, color: "#78716c" }}>{ROUND.myPoints.toLocaleString("en-US")} points · {ROUND.pointsToBronze} pt from bronze</div>
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#a8a29e", marginBottom: 4 }}>
                  <span>Your score</span>
                  <span>Bronze cut</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${Math.round((ROUND.myPoints / (ROUND.myPoints + ROUND.pointsToBronze)) * 100)}%` }} />
                </div>
              </div>
            </div>

            {/* Sponsor strip */}
            <div className="card" style={{ padding: "12px 14px" }}>
              <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#a8a29e", fontWeight: 600, marginBottom: 10 }}>SPONSORS · ROUND {ROUND.number}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
                {ROUND_SPONSOR_IDS.map((id) => (
                  <div key={id} style={{ height: 52, background: "#fff", border: "0.5px solid #ede8df", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", padding: 6 }}>
                    {SPONSORS[id].logo()}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ==================== PLAYER VIEW — drill-down fra leaderboard ==================== */}
        {view === "player" && selectedPlayer && PLAYER_DATA[selectedPlayer] && (() => {
          const player = PLAYER_DATA[selectedPlayer];
          const leaderboardRow = LEADERBOARD.find(r => r.name === selectedPlayer);
          const playerTier = leaderboardRow ? tierById(leaderboardRow.tier) : null;
          return (
            <div className="slide-in">
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <button className="btn-secondary" onClick={() => setView("leaderboard")}>← Leaderboard</button>
                <div>
                  <div style={{ fontSize: 11, color: "#a8a29e", letterSpacing: 1, fontWeight: 600 }}>PLAYER</div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{selectedPlayer}</div>
                </div>
              </div>

              {/* Hero profile card */}
              <div className="card" style={{ padding: 16, marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: 14,
                    background: player.avatar.bg,
                    color: player.avatar.color,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 20, fontWeight: 700, letterSpacing: 0.5,
                  }}>
                    {player.avatar.initials}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: "#1c1917", marginBottom: 2 }}>{selectedPlayer}</div>
                    <div style={{ fontSize: 12, color: "#a8a29e" }}>
                      Joined {player.joined} · {player.roundsPlayed} rounds played
                    </div>
                    {leaderboardRow && playerTier && (
                      <div style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 8px", background: playerTier.bg, color: playerTier.fg, borderRadius: 6, fontSize: 10, fontWeight: 700, letterSpacing: 1 }}>
                        #{leaderboardRow.rank} · {playerTier.name.toUpperCase()}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Stats grid 2x2 */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                <div style={{ background: "#fbf8f1", border: "0.5px solid #ece5d2", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#a8a29e", marginBottom: 4, fontWeight: 600 }}>BEST</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "#44403c", lineHeight: 1.1 }}>#{player.bestFinish.position}</div>
                  <div style={{ fontSize: 11, color: "#78716c", marginTop: 2 }}>round {player.bestFinish.round}</div>
                </div>
                <div style={{ background: "#fbf8f1", border: "0.5px solid #ece5d2", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#a8a29e", marginBottom: 4, fontWeight: 600 }}>AVG</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "#44403c", lineHeight: 1.1 }}>#{player.avgPosition}</div>
                  <div style={{ fontSize: 11, color: "#78716c", marginTop: 2 }}>across all rounds</div>
                </div>
                <div style={{ background: "#fbf8f1", border: "0.5px solid #ece5d2", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#a8a29e", marginBottom: 4, fontWeight: 600 }}>STREAK</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "#b45309", lineHeight: 1.1 }}>{player.streak}🔥</div>
                  <div style={{ fontSize: 11, color: "#78716c", marginTop: 2 }}>rounds in a row</div>
                </div>
                <div style={{ background: "#fbf8f1", border: "0.5px solid #ece5d2", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#a8a29e", marginBottom: 4, fontWeight: 600 }}>ROUNDS</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "#44403c", lineHeight: 1.1 }}>{player.roundsPlayed}</div>
                  <div style={{ fontSize: 11, color: "#78716c", marginTop: 2 }}>played in total</div>
                </div>
              </div>

              {/* Active round — LOCKED */}
              <div style={{ background: "#1c1917", borderRadius: 12, padding: "14px 16px", marginBottom: 14, position: "relative", overflow: "hidden" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(251,191,36,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="6" width="8" height="6" rx="1" fill="#fbbf24" />
                      <path d="M5 6 V4 a2 2 0 0 1 4 0 V6" stroke="#fbbf24" strokeWidth="1.4" fill="none" />
                    </svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, letterSpacing: 1.4, color: "#fbbf24", fontWeight: 600 }}>ROUND {ROUND.number} · LOCKED</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#fafaf9", marginTop: 1 }}>Picks are secret until the round ends</div>
                  </div>
                </div>
                <div style={{ borderTop: "1px solid #292524", paddingTop: 10, marginTop: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 11, color: "#a8a29e" }}>
                    {player.activeRound.picksMade}/{player.activeRound.of} matches picked · {player.activeRound.layersFilled}/{player.activeRound.of_layers} layers
                  </div>
                  <div style={{ fontSize: 11, color: "#fbbf24", fontWeight: 600 }}>
                    Opens in {countdown.d > 0 ? `${countdown.d}d ` : ""}{countdown.h}h {countdown.m}m
                  </div>
                </div>
              </div>

              {/* Round history */}
              <div style={{ fontSize: 11, color: "#a8a29e", letterSpacing: 1.2, fontWeight: 600, marginBottom: 8, padding: "0 4px" }}>
                PAST ROUNDS
              </div>

              {player.history.map((round) => {
                const roundTier = round.tier ? tierById(round.tier) : null;
                const isExpanded = !!expandedRounds[round.round];
                const hasPicks = !!round.picks;
                return (
                  <div key={round.round} className="card" style={{ padding: 0, marginBottom: 8, overflow: "hidden" }}>
                    <div
                      onClick={() => hasPicks && setExpandedRounds(s => ({ ...s, [round.round]: !s[round.round] }))}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "12px 14px",
                        cursor: hasPicks ? "pointer" : "default",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) => { if (hasPicks) e.currentTarget.style.background = "#faf7ef"; }}
                      onMouseLeave={(e) => { if (hasPicks) e.currentTarget.style.background = "transparent"; }}
                    >
                      <div style={{
                        width: 36, height: 36, borderRadius: 9,
                        background: roundTier ? roundTier.bg : "#f1ebd9",
                        color: roundTier ? roundTier.fg : "#78716c",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 11, fontWeight: 700, lineHeight: 1, marginRight: 12, flexShrink: 0,
                      }}>
                        #{round.position}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#1c1917" }}>Round {round.round}</div>
                        <div style={{ fontSize: 11, color: "#78716c" }}>
                          {round.points.toLocaleString("en-US")} points
                          {roundTier && <> · <span style={{ color: roundTier.labelColor, fontWeight: 700 }}>{roundTier.name.toUpperCase()}</span></>}
                        </div>
                      </div>
                      {hasPicks ? (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ display: "block", transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}
                        >
                          <path d="M3.5 5.25 L7 8.75 L10.5 5.25" stroke="#78716c" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : (
                        <div style={{ fontSize: 10, color: "#a8a29e", fontStyle: "italic" }}>no details</div>
                      )}
                    </div>

                    {isExpanded && hasPicks && (
                      <div style={{ borderTop: "1px solid #f5f1e8", padding: "10px 12px 12px", background: "#fdfbf5" }}>
                        {round.picks.map((pick, pidx) => {
                          const correctLayers = pick.layers.filter(l => l.result === "V").length;
                          const totalLayersResolvable = pick.layers.filter(l => !l.skipped).length;
                          return (
                            <div key={pidx} style={{
                              padding: "10px 12px",
                              background: "#fff",
                              borderRadius: 10,
                              marginBottom: pidx < round.picks.length - 1 ? 8 : 0,
                              border: pick.isBonus ? "0.5px solid #fcd34d" : "0.5px solid #ede8df",
                            }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "#1c1917", display: "flex", alignItems: "center", gap: 6 }}>
                                  {pick.match}
                                  {pick.isBonus && <span style={{ background: "#fef3c7", color: "#92400e", padding: "2px 6px", borderRadius: 4, fontSize: 9, fontWeight: 700, letterSpacing: 1 }}>1.5×</span>}
                                </div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: "#b45309" }}>+{pick.roundPoints} pt</div>
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                {pick.layers.map((layer, lidx) => {
                                  if (layer.skipped) {
                                    return (
                                      <div key={lidx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 11, color: "#a8a29e" }}>
                                        <div style={{ width: 16, height: 16, borderRadius: 4, background: "#f5f1e8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#78716c", flexShrink: 0 }}>🛡️</div>
                                        <div style={{ flex: 1, fontStyle: "italic" }}>Skip & lock (layer {lidx + 1})</div>
                                      </div>
                                    );
                                  }
                                  const win = layer.result === "V";
                                  return (
                                    <div key={lidx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                                      <div style={{
                                        width: 16, height: 16, borderRadius: 4,
                                        background: win ? "#16a34a" : "#dc2626",
                                        color: "#fff", fontSize: 10, fontWeight: 700,
                                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                                      }}>
                                        {win ? "W" : "L"}
                                      </div>
                                      <div style={{ flex: 1, fontSize: 12, color: "#44403c", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{layer.label}</div>
                                      <div style={{ fontSize: 11, color: "#78716c", fontWeight: 600 }}>{layer.odds}x</div>
                                    </div>
                                  );
                                })}
                              </div>
                              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, paddingTop: 8, borderTop: "1px dashed #ede8df", fontSize: 10, color: "#a8a29e" }}>
                                <span>{correctLayers}/{totalLayersResolvable} correct</span>
                                {pick.layers.some(l => l.skipped) && <span>safe-lock used</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              <div style={{ textAlign: "center", marginTop: 12, padding: "8px 0", fontSize: 11, color: "#a8a29e", lineHeight: 1.5 }}>
                Picks become public when the round ends<br />
                <span style={{ color: "#c8c2b3" }}>No one can copy your picks during an active round</span>
              </div>
            </div>
          );
        })()}

        {/* ==================== HISTORY VIEW — Mine spil ==================== */}
        {view === "history" && (
          <div className="slide-in">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: "#a8a29e", letterSpacing: 1, fontWeight: 600 }}>MY GAMES</div>
                <div style={{ fontWeight: 700, fontSize: 18 }}>Your history</div>
              </div>
            </div>

            {/* Hero profile card */}
            <div className="card" style={{ padding: 16, marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 14,
                  background: USER_PROFILE.avatar.bg,
                  color: USER_PROFILE.avatar.color,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 20, fontWeight: 700, letterSpacing: 0.5,
                }}>
                  {USER_PROFILE.avatar.initials}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 17, fontWeight: 700, color: "#1c1917", marginBottom: 2 }}>{USER_PROFILE.name}</div>
                  <div style={{ fontSize: 12, color: "#a8a29e" }}>
                    {currentHandle} · joined {currentJoined}
                  </div>
                  <div style={{ fontSize: 11, color: "#78716c", marginTop: 4 }}>
                    {Number(currentTotalPoints).toLocaleString("en-US")} points lifetime · #{ROUND.myPosition} in round {ROUND.number}
                  </div>
                </div>
              </div>
            </div>

            {/* Stats grid 2x2 */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
              <div style={{ background: "#fbf8f1", border: "0.5px solid #ece5d2", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#a8a29e", marginBottom: 4, fontWeight: 600 }}>BEST</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#44403c", lineHeight: 1.1 }}>#{USER_PROFILE.bestFinish.position}</div>
                <div style={{ fontSize: 11, color: "#78716c", marginTop: 2 }}>round {USER_PROFILE.bestFinish.round}</div>
              </div>
              <div style={{ background: "#fbf8f1", border: "0.5px solid #ece5d2", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#a8a29e", marginBottom: 4, fontWeight: 600 }}>AVG</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#44403c", lineHeight: 1.1 }}>#{USER_PROFILE.avgPosition}</div>
                <div style={{ fontSize: 11, color: "#78716c", marginTop: 2 }}>across all rounds</div>
              </div>
              <div style={{ background: "#fbf8f1", border: "0.5px solid #ece5d2", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#a8a29e", marginBottom: 4, fontWeight: 600 }}>STREAK</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#b45309", lineHeight: 1.1 }}>{USER_PROFILE.streak}🔥</div>
                <div style={{ fontSize: 11, color: "#78716c", marginTop: 2 }}>rounds in a row</div>
              </div>
              <div style={{ background: "#fbf8f1", border: "0.5px solid #ece5d2", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#a8a29e", marginBottom: 4, fontWeight: 600 }}>ROUNDS</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#44403c", lineHeight: 1.1 }}>{USER_PROFILE.roundsPlayed}</div>
                <div style={{ fontSize: 11, color: "#78716c", marginTop: 2 }}>played in total</div>
              </div>
            </div>

            {/* Tier wins strip */}
            <div className="card" style={{ padding: "12px 14px", marginBottom: 14 }}>
              <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#a8a29e", fontWeight: 600, marginBottom: 10 }}>TIERS WON · LIFETIME</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                {PRIZE_TIERS.map((tier) => {
                  const wins = USER_PROFILE.tierWins[tier.id] || 0;
                  const hasWins = wins > 0;
                  return (
                    <div key={tier.id} style={{
                      padding: "10px 6px",
                      borderRadius: 8,
                      background: hasWins ? tier.bg : "#faf7ef",
                      border: hasWins ? "none" : "0.5px dashed #ede8df",
                      textAlign: "center",
                      opacity: hasWins ? 1 : 0.55,
                    }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: hasWins ? tier.fg : "#a8a29e", lineHeight: 1 }}>
                        {wins}
                      </div>
                      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1, color: hasWins ? tier.fg : "#a8a29e", marginTop: 4 }}>
                        {tier.name.toUpperCase()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Active round 24 — visible since this is YOUR data */}
            <div style={{ fontSize: 11, color: "#a8a29e", letterSpacing: 1.2, fontWeight: 600, marginBottom: 8, padding: "0 4px" }}>
              CURRENT ROUND
            </div>
            <div className="card" style={{ padding: 0, marginBottom: 14, overflow: "hidden", border: "1px solid #fcd34d" }}>
              <div style={{ padding: "12px 14px", background: "linear-gradient(135deg, #fffbeb 0%, #fff 80%)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, letterSpacing: 1.4, color: "#92400e", fontWeight: 700 }}>ROUND {ROUND.number} · IN PROGRESS</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#1c1917", marginTop: 2 }}>
                      {numPicked > 0 ? `${numPicked}/${ROUND.maxPicks} matches picked · ${totalPoints} pt potential` : "You haven't picked any matches yet"}
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: "#78716c", textAlign: "right" }}>
                    Closes in<br />
                    <span style={{ color: "#b45309", fontWeight: 700, fontSize: 12 }}>
                      {countdown.d > 0 ? `${countdown.d}d ` : ""}{countdown.h}h {countdown.m}m
                    </span>
                  </div>
                </div>
                {numPicked > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                    {Object.entries(picks).map(([mId, p]) => {
                      const match = MATCHES.find(m => m.id === mId);
                      if (!match) return null;
                      const matchPoints = p.selections.reduce((s, sel) => s + Math.floor(sel.odds * 100), 0);
                      const finalPoints = match.isBonus ? Math.floor(matchPoints * 1.5) : matchPoints;
                      const wasSkipped = p.selections.length === 2;
                      return (
                        <div key={mId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "#fff", borderRadius: 8, border: "0.5px solid #f5f1e8" }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#1c1917", display: "flex", alignItems: "center", gap: 6 }}>
                              {match.homeShort} vs {match.awayShort}
                              {match.isBonus && <span style={{ background: "#fef3c7", color: "#92400e", padding: "1px 5px", borderRadius: 3, fontSize: 9, fontWeight: 700, letterSpacing: 1 }}>1.5×</span>}
                              {wasSkipped && <span style={{ background: "#f5f1e8", color: "#78716c", padding: "1px 5px", borderRadius: 3, fontSize: 9, fontWeight: 700, letterSpacing: 1 }}>SAFE</span>}
                            </div>
                            <div style={{ fontSize: 10, color: "#a8a29e", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {p.selections.map(s => s.label).join(" · ")}
                            </div>
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#b45309", flexShrink: 0 }}>+{finalPoints}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <button
                    className="btn-primary"
                    style={{ width: "100%", marginTop: 6 }}
                    onClick={() => setView("lobby")}
                  >
                    Pick your matches →
                  </button>
                )}
                {numPicked > 0 && !submitted && (
                  <button
                    onClick={() => setView("lobby")}
                    style={{
                      width: "100%",
                      marginTop: 10,
                      padding: "8px 12px",
                      background: "transparent",
                      border: "0.5px solid #d6cdb6",
                      borderRadius: 8,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#78716c",
                    }}
                  >
                    Edit picks →
                  </button>
                )}
                {submitted && (
                  <div style={{ marginTop: 10, padding: "8px 12px", background: "#dcfce7", borderRadius: 8, fontSize: 11, color: "#166534", textAlign: "center", fontWeight: 600 }}>
                    ✓ Picks saved · editable until each kickoff
                  </div>
                )}
              </div>
            </div>

            {/* Round history */}
            <div style={{ fontSize: 11, color: "#a8a29e", letterSpacing: 1.2, fontWeight: 600, marginBottom: 8, padding: "0 4px" }}>
              PAST ROUNDS
            </div>

            {USER_HISTORY.map((round) => {
              const roundTier = round.tier ? tierById(round.tier) : null;
              const isExpanded = !!expandedRounds[`u${round.round}`];
              const hasPicks = !!round.picks;
              return (
                <div key={round.round} className="card" style={{ padding: 0, marginBottom: 8, overflow: "hidden" }}>
                  <div
                    onClick={() => hasPicks && setExpandedRounds(s => ({ ...s, [`u${round.round}`]: !s[`u${round.round}`] }))}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "12px 14px",
                      cursor: hasPicks ? "pointer" : "default",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => { if (hasPicks) e.currentTarget.style.background = "#faf7ef"; }}
                    onMouseLeave={(e) => { if (hasPicks) e.currentTarget.style.background = "transparent"; }}
                  >
                    <div style={{
                      width: 36, height: 36, borderRadius: 9,
                      background: roundTier ? roundTier.bg : "#f1ebd9",
                      color: roundTier ? roundTier.fg : "#78716c",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, fontWeight: 700, lineHeight: 1, marginRight: 12, flexShrink: 0,
                    }}>
                      #{round.position}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#1c1917" }}>Round {round.round}</div>
                      <div style={{ fontSize: 11, color: "#78716c" }}>
                        {round.points.toLocaleString("en-US")} points
                        {roundTier && <> · <span style={{ color: roundTier.labelColor, fontWeight: 700 }}>{roundTier.name.toUpperCase()}</span></>}
                      </div>
                    </div>
                    {hasPicks ? (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ display: "block", transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}
                      >
                        <path d="M3.5 5.25 L7 8.75 L10.5 5.25" stroke="#78716c" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <div style={{ fontSize: 10, color: "#a8a29e", fontStyle: "italic" }}>summary</div>
                    )}
                  </div>

                  {isExpanded && hasPicks && (
                    <div style={{ borderTop: "1px solid #f5f1e8", padding: "10px 12px 12px", background: "#fdfbf5" }}>
                      {round.picks.map((pick, pidx) => {
                        const correctLayers = pick.layers.filter(l => l.result === "V").length;
                        const totalLayersResolvable = pick.layers.filter(l => !l.skipped).length;
                        return (
                          <div key={pidx} style={{
                            padding: "10px 12px",
                            background: "#fff",
                            borderRadius: 10,
                            marginBottom: pidx < round.picks.length - 1 ? 8 : 0,
                            border: pick.isBonus ? "0.5px solid #fcd34d" : "0.5px solid #ede8df",
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "#1c1917", display: "flex", alignItems: "center", gap: 6 }}>
                                {pick.match}
                                {pick.isBonus && <span style={{ background: "#fef3c7", color: "#92400e", padding: "2px 6px", borderRadius: 4, fontSize: 9, fontWeight: 700, letterSpacing: 1 }}>1.5×</span>}
                              </div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: pick.roundPoints > 0 ? "#b45309" : "#a8a29e" }}>+{pick.roundPoints} pt</div>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              {pick.layers.map((layer, lidx) => {
                                if (layer.skipped) {
                                  return (
                                    <div key={lidx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 11, color: "#a8a29e" }}>
                                      <div style={{ width: 16, height: 16, borderRadius: 4, background: "#f5f1e8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#78716c", flexShrink: 0 }}>🛡️</div>
                                      <div style={{ flex: 1, fontStyle: "italic" }}>Skip & lock (layer {lidx + 1})</div>
                                    </div>
                                  );
                                }
                                const win = layer.result === "V";
                                return (
                                  <div key={lidx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                                    <div style={{
                                      width: 16, height: 16, borderRadius: 4,
                                      background: win ? "#16a34a" : "#dc2626",
                                      color: "#fff", fontSize: 10, fontWeight: 700,
                                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                                    }}>
                                      {win ? "W" : "L"}
                                    </div>
                                    <div style={{ flex: 1, fontSize: 12, color: "#44403c", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{layer.label}</div>
                                    <div style={{ fontSize: 11, color: "#78716c", fontWeight: 600 }}>{layer.odds}x</div>
                                  </div>
                                );
                              })}
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, paddingTop: 8, borderTop: "1px dashed #ede8df", fontSize: 10, color: "#a8a29e" }}>
                              <span>{correctLayers}/{totalLayersResolvable} correct</span>
                              {pick.layers.some(l => l.skipped) && <span>safe-lock used</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ==================== PROFILE VIEW ==================== */}
        {view === "profile" && (
          <div className="slide-in">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: "#a8a29e", letterSpacing: 1, fontWeight: 600 }}>PROFILE</div>
                <div style={{ fontWeight: 700, fontSize: 18 }}>{profileName}</div>
              </div>
            </div>

            {/* Identity card — avatar + name + handle, with edit affordances */}
            <div className="card" style={{ padding: 18, marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                {/* Avatar — uploaded image OR initials fallback */}
                <label style={{ position: "relative", cursor: "pointer", flexShrink: 0 }}>
                  {profileAvatarUrl ? (
                    <img
                      src={profileAvatarUrl}
                      alt="avatar"
                      style={{ width: 72, height: 72, borderRadius: 18, objectFit: "cover", display: "block" }}
                    />
                  ) : (
                    <div style={{
                      width: 72, height: 72, borderRadius: 18,
                      background: USER_PROFILE.avatar.bg,
                      color: USER_PROFILE.avatar.color,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 26, fontWeight: 700, letterSpacing: 0.5,
                    }}>
                      {USER_PROFILE.avatar.initials}
                    </div>
                  )}
                  {/* camera badge */}
                  <div style={{
                    position: "absolute", bottom: -2, right: -2,
                    width: 26, height: 26, borderRadius: 13,
                    background: "#1c1917", color: "#fbf8f1",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 13, border: "2px solid #fbf8f1",
                  }}>📷</div>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files && e.target.files[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = () => setProfileAvatarUrl(reader.result);
                      reader.readAsDataURL(file);
                    }}
                  />
                </label>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {editingName ? (
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input
                        autoFocus
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const trimmed = nameDraft.trim();
                            if (trimmed) commitName(trimmed);
                            setEditingName(false);
                          } else if (e.key === "Escape") {
                            setNameDraft(profileName);
                            setEditingName(false);
                          }
                        }}
                        maxLength={32}
                        style={{
                          flex: 1, minWidth: 0,
                          fontSize: 17, fontWeight: 700, color: "#1c1917",
                          background: "#fbf8f1", border: "1px solid #ede8df",
                          borderRadius: 8, padding: "6px 10px", outline: "none",
                        }}
                      />
                      <button
                        onClick={() => {
                          const trimmed = nameDraft.trim();
                          if (trimmed) commitName(trimmed);
                          setEditingName(false);
                        }}
                        style={{ background: "#b45309", color: "#fff", border: "none", borderRadius: 8, padding: "6px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                      >Save</button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: "#1c1917" }}>{profileName}</div>
                      <button
                        onClick={() => { setNameDraft(profileName); setEditingName(true); }}
                        title="Change name"
                        style={{ background: "transparent", border: "none", color: "#a8a29e", cursor: "pointer", fontSize: 14, padding: 4 }}
                      >✏️</button>
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: "#a8a29e", marginTop: 2 }}>
                    {currentHandle} · joined {currentJoined}
                  </div>
                </div>
              </div>
            </div>

            {/* Trophy row — lifetime tier wins */}
            <div className="card" style={{ padding: 16, marginBottom: 12 }}>
              <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#a8a29e", fontWeight: 600, marginBottom: 10 }}>TROPHIES</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                {PRIZE_TIERS.map(tier => {
                  const wins = USER_PROFILE.tierWins[tier.id] || 0;
                  const earned = wins > 0;
                  return (
                    <div key={tier.id} style={{
                      textAlign: "center",
                      padding: "10px 4px",
                      borderRadius: 10,
                      background: earned ? "#fbf8f1" : "transparent",
                      border: earned ? "0.5px solid #ece5d2" : "0.5px dashed #ece5d2",
                      opacity: earned ? 1 : 0.45,
                    }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: 18,
                        background: tier.bg, color: tier.fg,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 16, fontWeight: 700,
                        margin: "0 auto 6px",
                      }}>{tier.letter}</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: earned ? "#1c1917" : "#a8a29e", lineHeight: 1 }}>
                        {wins}×
                      </div>
                      <div style={{ fontSize: 10, color: "#78716c", marginTop: 3, letterSpacing: 0.3 }}>{tier.name}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Headline stats — streak + lifetime points */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
              <div style={{ background: "linear-gradient(135deg, #fff7ed, #fef3c7)", border: "0.5px solid #fde68a", borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#92400e", fontWeight: 600, marginBottom: 6 }}>STREAK</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: "#b45309", lineHeight: 1 }}>
                  🔥 {USER_PROFILE.streak}
                </div>
                <div style={{ fontSize: 11, color: "#92400e", marginTop: 4 }}>rounds in a row</div>
              </div>
              <div style={{ background: "#fbf8f1", border: "0.5px solid #ece5d2", borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#a8a29e", fontWeight: 600, marginBottom: 6 }}>LIFETIME POINTS</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: "#1c1917", lineHeight: 1 }}>
                  {Number(currentTotalPoints).toLocaleString("en-US")}
                </div>
                <div style={{ fontSize: 11, color: "#78716c", marginTop: 4 }}>across {USER_PROFILE.roundsPlayed} rounds</div>
              </div>
            </div>

            {/* Prize inventory — vises kun hvis der er noget. Skjuler unclaimed (de er på lobby-banneret). */}
            {wins.filter(w => w.status !== "unclaimed").length > 0 && (
              <div className="card" style={{ padding: 16, marginBottom: 12 }}>
                <div style={{ fontSize: 10, letterSpacing: 1.2, color: "#a8a29e", fontWeight: 600, marginBottom: 10 }}>YOUR PRIZES</div>
                {wins.filter(w => w.status !== "unclaimed").map((prize, i, arr) => {
                  const sponsorName = prize.sponsorId ? SPONSORS[prize.sponsorId].name : "Pickfox";
                  return (
                    <div key={prize.id} style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "10px 0",
                      borderBottom: i < arr.length - 1 ? "0.5px solid #ece5d2" : "none",
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "#1c1917" }}>{prize.prizeLabel}</div>
                        <div style={{ fontSize: 11, color: "#78716c", marginTop: 2 }}>Round {prize.roundNumber} · {sponsorName}</div>
                      </div>
                      <div style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                        padding: "4px 8px", borderRadius: 6,
                        background: prize.status === "redeemed" ? "#dcfce7" : "#fef3c7",
                        color: prize.status === "redeemed" ? "#166534" : "#92400e",
                      }}>
                        {prize.status === "redeemed" ? "REDEEMED" : "SENT"}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Settings */}
            <div className="card" style={{ padding: 4, marginBottom: 12 }}>
              {[
                { key: "notifications", label: "Notifications", sub: "Round start, lock, results", type: "toggle" },
                { key: "picksPublic", label: "Picks visible after lock", sub: "Others can see what you picked", type: "toggle" },
                { key: "newsletter", label: "Newsletter", sub: "Sponsor offers + features", type: "toggle" },
              ].map((row, i, arr) => (
                <div key={row.key} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "12px 14px",
                  borderBottom: i < arr.length - 1 ? "0.5px solid #ece5d2" : "none",
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#1c1917" }}>{row.label}</div>
                    <div style={{ fontSize: 11, color: "#78716c", marginTop: 2 }}>{row.sub}</div>
                  </div>
                  {row.type === "toggle" && (
                    <button
                      onClick={() => setSettings(s => ({ ...s, [row.key]: !s[row.key] }))}
                      style={{
                        width: 44, height: 26, borderRadius: 13,
                        border: "none", cursor: "pointer",
                        background: settings[row.key] ? "#b45309" : "#d6d3d1",
                        position: "relative", transition: "background 0.15s",
                        padding: 0, flexShrink: 0,
                      }}
                      aria-pressed={settings[row.key]}
                    >
                      <div style={{
                        position: "absolute", top: 3, left: settings[row.key] ? 21 : 3,
                        width: 20, height: 20, borderRadius: 10,
                        background: "#fff",
                        transition: "left 0.15s",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      }} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Help + account actions */}
            <div className="card" style={{ padding: 4, marginBottom: 32 }}>
              {[
                ...(onOpenOperator ? [{ label: "Operator panel", sub: "Manage rounds & outcomes", action: onOpenOperator, color: "#b45309" }] : []),
                { label: "Help & FAQ", sub: "How does stacking work?", action: () => alert("FAQ opens later") },
                { label: "Contact support", sub: "We reply within 24 hours", action: () => alert("Support: support@pickfox.io") },
                { label: "Log out", sub: null, action: () => { if (window.confirm("Log out of Pickfox?")) supabase.auth.signOut(); }, color: "#78716c" },
                { label: "Delete account", sub: null, action: () => alert("Delete account flow comes later"), color: "#dc2626" },
              ].map((row, i, arr) => (
                <div
                  key={row.label}
                  onClick={row.action}
                  style={{
                    padding: "12px 14px",
                    borderBottom: i < arr.length - 1 ? "0.5px solid #ece5d2" : "none",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#faf7ef"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  <div style={{ fontSize: 14, fontWeight: 600, color: row.color || "#1c1917" }}>{row.label}</div>
                  {row.sub && <div style={{ fontSize: 11, color: "#78716c", marginTop: 2 }}>{row.sub}</div>}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={{ textAlign: "center", fontSize: 10, color: "#a8a29e", marginBottom: 80, letterSpacing: 0.5 }}>
              Pickfox v0.1 · pickfox.io
            </div>
          </div>
        )}

        {/* ==================== CLAIM VIEW — formular + success ==================== */}
        {view === "claim" && (() => {
          const win = wins.find(w => w.id === claimingWinId);
          if (!win) {
            // Edge case: claim'er ud i ingenting — gå tilbage
            return (
              <div className="slide-in" style={{ padding: "40px 0", textAlign: "center" }}>
                <div style={{ fontSize: 14, color: "#78716c", marginBottom: 16 }}>No active prize to redeem.</div>
                <button className="btn-primary" onClick={() => setView("lobby")}>Back to home</button>
              </div>
            );
          }
          const tier = tierById(win.tier);
          const sponsor = win.sponsorId ? SPONSORS[win.sponsorId] : null;
          const isSuccess = justClaimedWinId === win.id;

          return (
            <div className="slide-in">
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <button className="btn-secondary" onClick={() => { setView("lobby"); setJustClaimedWinId(null); }}>← Back</button>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: "#a8a29e", letterSpacing: 1, fontWeight: 600 }}>{isSuccess ? "PRIZE REDEEMED" : "REDEEM PRIZE"}</div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{tier.name} · Round {win.roundNumber}</div>
                </div>
              </div>

              {/* Prize summary card */}
              <div className="card" style={{ padding: 18, marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 22,
                    background: tier.bg, color: tier.fg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 18, fontWeight: 700, flexShrink: 0,
                  }}>{tier.letter}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#1c1917" }}>{win.prizeLabel}</div>
                    <div style={{ fontSize: 12, color: "#78716c", marginTop: 2 }}>{win.prizeDetail}</div>
                  </div>
                </div>
                {sponsor && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 10, borderTop: "0.5px solid #ece5d2" }}>
                    <span style={{ fontSize: 11, color: "#a8a29e" }}>Delivered by</span>
                    {sponsor.logo({ scale: 0.9 })}
                  </div>
                )}
              </div>

              {!isSuccess && (
                <>
                  {/* Form */}
                  <div className="card" style={{ padding: 18, marginBottom: 14 }}>
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ fontSize: 11, letterSpacing: 1, color: "#a8a29e", fontWeight: 600, display: "block", marginBottom: 6 }}>EMAIL FOR DELIVERY</label>
                      <input
                        type="email"
                        value={claimDraft.email}
                        onChange={(e) => setClaimDraft(d => ({ ...d, email: e.target.value }))}
                        style={{
                          width: "100%", boxSizing: "border-box",
                          fontSize: 14, color: "#1c1917",
                          background: "#fbf8f1", border: "1px solid #ede8df",
                          borderRadius: 8, padding: "10px 12px", outline: "none",
                        }}
                      />
                      <div style={{ fontSize: 11, color: "#78716c", marginTop: 4 }}>
                        {sponsor ? `${sponsor.name} will send confirmation here.` : "We'll send the codes here."}
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, letterSpacing: 1, color: "#a8a29e", fontWeight: 600, display: "block", marginBottom: 6 }}>MESSAGE (optional)</label>
                      <textarea
                        value={claimDraft.message}
                        onChange={(e) => setClaimDraft(d => ({ ...d, message: e.target.value }))}
                        rows={3}
                        placeholder="E.g. specific date preferences or any questions"
                        style={{
                          width: "100%", boxSizing: "border-box", resize: "vertical",
                          fontSize: 13, color: "#1c1917",
                          background: "#fbf8f1", border: "1px solid #ede8df",
                          borderRadius: 8, padding: "10px 12px", outline: "none",
                          fontFamily: "inherit",
                        }}
                      />
                    </div>
                  </div>

                  <button
                    className="btn-primary"
                    style={{ width: "100%", marginBottom: 12 }}
                    onClick={async () => {
                      const { error } = await supabase
                        .from("prize_wins")
                        .update({
                          status: "claimed",
                          email_for_delivery: claimDraft.email,
                          claim_message: claimDraft.message || null,
                        })
                        .eq("id", win.id);
                      if (error) {
                        console.error("Failed to claim prize:", error);
                        alert("Could not claim: " + error.message);
                        return;
                      }
                      setWins(prev => prev.map(w => w.id === win.id ? {
                        ...w,
                        status: "claimed",
                        claimedAt: new Date().toISOString().slice(0, 10),
                      } : w));
                      setJustClaimedWinId(win.id);
                    }}
                    disabled={!/^\S+@\S+\.\S+$/.test(claimDraft.email)}
                  >
                    Redeem prize
                  </button>
                  <div style={{ fontSize: 11, color: "#78716c", textAlign: "center", marginBottom: 32 }}>
                    Expires {win.expiresAt}
                  </div>
                </>
              )}

              {isSuccess && (
                <>
                  <div className="card" style={{ padding: 18, marginBottom: 14, textAlign: "center" }}>
                    <div style={{ fontSize: 48, marginBottom: 8 }}>✅</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "#1c1917", marginBottom: 6 }}>Redeemed!</div>
                    <div style={{ fontSize: 13, color: "#78716c", lineHeight: 1.5 }}>
                      {sponsor
                        ? `${sponsor.name} will send a confirmation to ${claimDraft.email} within 1-2 business days.`
                        : `You'll receive your discount codes at ${claimDraft.email}.`}
                    </div>
                    {win.code && (
                      <div style={{
                        marginTop: 14, padding: "10px 14px",
                        background: "#fbf8f1", border: "0.5px dashed #ece5d2", borderRadius: 8,
                        fontFamily: "ui-monospace, SFMono-Regular, monospace",
                        fontSize: 13, fontWeight: 600, color: "#b45309",
                        letterSpacing: 0.5,
                      }}>
                        {win.code}
                      </div>
                    )}
                  </div>
                  <button className="btn-primary" style={{ width: "100%", marginBottom: 32 }} onClick={() => { setView("lobby"); setJustClaimedWinId(null); setClaimingWinId(null); }}>
                    Back to home
                  </button>
                </>
              )}
            </div>
          );
        })()}

        {/* ==================== SUBMITTED VIEW ==================== */}
        {view === "submitted" && (
          <div className="slide-in" style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: 80, marginBottom: 10 }}>🦊</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: "#b45309", marginBottom: 6 }}>Picks saved!</div>
            <div style={{ fontSize: 13, color: "#78716c", marginBottom: 24, padding: "0 24px", lineHeight: 1.5 }}>
              You have {totalPoints} points at stake. You can still edit a pick until its match starts.
            </div>
            <button className="btn-primary" onClick={() => setView("lobby")}>Back to home</button>
          </div>
        )}

      </div>

      {/* ==================== LEVEL UP MODAL — auto-dismisses after 3.5s ==================== */}
      {levelUpShow !== null && (
        <div
          onClick={() => setLevelUpShow(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 100,
            background: "rgba(28, 25, 23, 0.55)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            animation: "fadeIn 0.25s ease-out",
            cursor: "pointer",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
              border: "1px solid #fcd34d",
              borderRadius: 22,
              padding: "32px 42px",
              textAlign: "center",
              boxShadow: "0 24px 64px rgba(120, 90, 0, 0.25)",
              animation: "popIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              minWidth: 260,
            }}
          >
            <div style={{ fontSize: 70, marginBottom: 6, lineHeight: 1 }}>🎉</div>
            <div style={{ fontSize: 11, letterSpacing: 1.5, color: "#92400e", fontWeight: 700, marginBottom: 4 }}>LEVEL UP</div>
            <div style={{ fontSize: 38, fontWeight: 800, color: "#b45309", letterSpacing: -0.5, lineHeight: 1 }}>Level {levelUpShow}</div>
            <div style={{ fontSize: 12, color: "#78716c", marginTop: 8 }}>{xp.toLocaleString("en-US")} XP total</div>
          </div>
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes popIn {
              0% { transform: scale(0.7); opacity: 0; }
              60% { transform: scale(1.05); opacity: 1; }
              100% { transform: scale(1); opacity: 1; }
            }
          `}</style>
        </div>
      )}

      {/* ==================== BOTTOM NAV — hidden when submit-bar is active ==================== */}
      {!(view === "round" && numPicked > 0) && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(251,248,241,0.94)", backdropFilter: "blur(20px)", borderTop: "1px solid #ede8df", padding: "10px 0 18px", zIndex: 40 }}>
          <div style={{ maxWidth: 480, margin: "0 auto", display: "flex", justifyContent: "space-around", padding: "0 16px" }}>
            {[
              { icon: "🏠", label: "Home", view: "lobby" },
              { icon: "🏆", label: "Leaderboard", view: "leaderboard" },
              { icon: "📊", label: "My games", view: "history" },
              { icon: "👤", label: "Profile", view: "profile" },
            ].map((item) => {
              const active = view === item.view || (item.view === "lobby" && ["round","match","confirm","submitted"].includes(view)) || (item.view === "leaderboard" && view === "player");
              return (
                <div key={item.label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", opacity: active ? 1 : 0.5 }} onClick={() => item.view && setView(item.view)}>
                  <span style={{ fontSize: 20 }}>{item.icon}</span>
                  <span style={{ fontSize: 10, letterSpacing: 1, color: active ? "#b45309" : "#a8a29e", fontWeight: active ? 700 : 500 }}>{item.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ==================== WIN MODAL — celebration takeover ==================== */}
      {activeWinId && (() => {
        const win = wins.find(w => w.id === activeWinId);
        if (!win) return null;
        const tier = tierById(win.tier);
        const sponsor = win.sponsorId ? SPONSORS[win.sponsorId] : null;
        const close = () => setActiveWinId(null);
        return (
          <div
            onClick={close}
            style={{
              position: "fixed", inset: 0, zIndex: 100,
              background: "rgba(28, 25, 23, 0.62)",
              backdropFilter: "blur(8px)",
              animation: "backdropFadeIn 0.2s ease forwards",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: "20px",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%", maxWidth: 420,
                background: "linear-gradient(180deg, #fbf8f1, #fff7ed)",
                borderRadius: 22,
                padding: "32px 26px 24px",
                boxShadow: "0 24px 64px rgba(0,0,0,0.32)",
                animation: "modalRise 0.32s cubic-bezier(0.34, 1.4, 0.64, 1) forwards",
                textAlign: "center",
                position: "relative",
              }}
            >
              {/* Pickfox-first headline */}
              <div style={{ fontSize: 11, letterSpacing: 2, color: "#a8a29e", fontWeight: 600, marginBottom: 6 }}>
                PICKFOX · ROUND {win.roundNumber}
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: "#1c1917", marginBottom: 22 }}>
                You won {tier.name}!
              </div>

              {/* Animated trophy */}
              <div style={{
                margin: "0 auto 22px",
                width: 110, height: 110, borderRadius: 55,
                background: tier.bg, color: tier.fg,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 48, fontWeight: 800,
                animation: "trophyIn 0.55s cubic-bezier(0.34, 1.5, 0.64, 1) forwards, badgeGlow 2.4s ease-in-out 0.6s infinite",
              }}>
                {tier.letter}
              </div>

              {/* Prize card */}
              <div style={{
                background: "#fff",
                border: "0.5px solid #ece5d2",
                borderRadius: 14,
                padding: "16px 18px",
                marginBottom: 18,
                textAlign: "left",
              }}>
                <div style={{ fontSize: 10, letterSpacing: 1.4, color: "#a8a29e", fontWeight: 600, marginBottom: 4 }}>YOUR PRIZE</div>
                <div style={{ fontSize: 17, fontWeight: 700, color: "#1c1917", marginBottom: 3 }}>{win.prizeLabel}</div>
                <div style={{ fontSize: 12, color: "#78716c" }}>{win.prizeDetail}</div>
                {sponsor && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "0.5px solid #ece5d2", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 10, letterSpacing: 1, color: "#a8a29e", fontWeight: 600 }}>IN PARTNERSHIP WITH</span>
                    {sponsor.logo({ scale: 0.85 })}
                  </div>
                )}
              </div>

              {/* CTAs */}
              <button
                className="btn-primary"
                style={{ width: "100%", marginBottom: 8 }}
                onClick={() => {
                  setClaimingWinId(win.id);
                  setActiveWinId(null);
                  setView("claim");
                }}
              >
                Redeem prize
              </button>
              <button
                onClick={close}
                style={{
                  background: "transparent", border: "none",
                  color: "#78716c", fontSize: 13, fontWeight: 500,
                  cursor: "pointer", padding: "8px 12px",
                }}
              >
                Later
              </button>

              <div style={{ fontSize: 11, color: "#a8a29e", marginTop: 6 }}>
                Expires {win.expiresAt}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
